import { skuAction } from "./salesPresentation";
import type { RelatedContext } from './api/contracts';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  ArrowRightIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import {
  BottomSheet,
  Carousel,
  FlowStack,
  KeyboardInput,
  KeyboardTextarea,
  MobileScroll,
  useKeyboard,
  type FlowControls,
  type FlowScreen,
} from "./h5-runtime";
import { productSearchApi, ProductSearchApiError } from "./api/client";
import { toProduct, toProducts } from "./api/adapters";
import { openPrivacyPolicy } from "./privacyNavigation";
import { isMiniProgramChannel, openProductDestination } from "./productNavigation";
import {
  recommendationPurposes,
  type RecommendationContext,
  type RecommendationPurpose,
} from "./recommendationDraft.ts";
import {
  clearRecommendationDraft,
  loadRecommendationDraft,
  updateRecommendationDraft,
} from "./recommendationPreferenceStore.ts";
import {
  cachedBrandDirectory,
  filterBrandOptions,
  groupBrandOptions,
  loadBrandDirectory,
  normalizeBrandText,
  recommendableBrands,
  type BrandOption,
} from "./brandDirectory.ts";
import { hasReviewRating, reviewCountLabel } from "./reviewPresentation";
import { hasSelectedBrand, partitionRecommendations } from "./recommendationPresentation.ts";
import { CameraIcon } from "./CameraIcon";
import { ImageCaptureGuide } from "./ImageCaptureGuide";
import { skuInStock, saleCopy, skuCanOpen, aiErrorCopy, productAction, productSaleCopy, orderedFilterOptions } from "./salesPresentation";
import { readImageFile } from "./imageInput";
import { partitionSearchResults, productPage, parsePageNumber } from "./searchPresentation";
import { cleanAIFindQuery, flattenAIFindProducts, imageResultTitle } from "./aiResultPresentation";
import type { AIFindResponse, SimilarResponse, SearchResponse } from "./api/contracts";
import type { Product, Sku } from "./product-types";
import { WishlistProvider, useWishlist } from "./wishlist/WishlistStore";
import { WishlistEntry } from "./wishlist/WishlistEntry";
import { makeWishlistScreen } from "./wishlist/WishlistPage";
import { wishlistApi } from "./wishlist/wishlistApi";
import "./wishlist/wishlist.css";
import "./wishlist/wishlist-responsive.css";
import "./wishlist/wishlist-dialog.css";
import "./wishlist/wishlist-fixed-layout.css";

type CollectionMode = "brand" | "category" | "keyword" | "promotion" | "image";

type ProductDetailSource = {
  kind: "featured" | "search" | "recommendation";
  label: string;
};

function recommendationContextSummary(context: RecommendationContext): string {
  return [context.purpose, context.scent, context.budget, context.brand]
    .map((value) => String(value || "").trim())
    .filter((value) => value && !value.startsWith("不限"))
    .join(" · ");
}

type CatalogCollectionPage = {
  query: string;
  total: number;
  hasMore: boolean;
  nextOffset: number;
  referenceKey?: string | null;
  confirmBrand?: boolean;
  literal?: boolean;
  matchType?: SearchResponse["matchType"];
  originalQuery?: string;
  resolvedQuery?: string;
};

type SearchResolution =
  | { mode: "product"; product: Product }
  | { mode: "brand"; brand: string; products: Product[]; page?: CatalogCollectionPage }
  | { mode: "category"; category: string; products: Product[]; page?: CatalogCollectionPage }
  | { mode: "keyword"; keyword: string; products: Product[]; page: CatalogCollectionPage }
  | { mode: "promotion"; keyword: string; collection: string; products: Product[]; page: CatalogCollectionPage }
  | { mode: "clarify"; suggestions: SearchSuggestion[] };

type SearchSuggestion = {
  query: string;
  label: string;
  hint: string;
  kind: "品牌" | "香调" | "商品";
  matchType?: SearchResponse["matchType"];
};

type SheetState = {
  actionHref?: string;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
} | null;

const fallbackProducts: Product[] = [
  {
    id: "bn-1001",
    brand: "BYREDO",
    name: "Blanche",
    chineseName: "白色浪漫",
    image: "/assets/products/byredo-blanche.png",
    tags: ["干净皂感", "花香调", "日常通勤"],
    reason: "清透皂感与柔和白花，适合喜欢干净气息的人",
    isListed: true,
    hasStock: true,
    saleStatus: "available",
    listedAt: "2026-08-11",
    skus: [
      { id: "bn-1001-s1", packageType: "试香装", capacity: "2ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
      { id: "bn-1001-s2", packageType: "旅行装", capacity: "12ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
      { id: "bn-1001-s3", packageType: "淡香精正装", capacity: "50ml", stock: "少量", availability: "low_stock", saleStatus: "available" },
      { id: "bn-1001-s4", packageType: "淡香精正装", capacity: "100ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
    ],
  },
  {
    id: "bn-1002",
    brand: "DIPTYQUE",
    name: "Philosykos",
    chineseName: "希腊无花果",
    image: "/assets/products/diptyque-philosykos.png",
    tags: ["无花果", "绿意木质", "春夏"],
    reason: "青绿无花果叶与温柔木质感，清新但不单薄",
    isListed: true,
    hasStock: true,
    saleStatus: "available",
    listedAt: "2026-08-10",
    skus: [
      { id: "bn-1002-s1", packageType: "试香装", capacity: "2ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
      { id: "bn-1002-s2", packageType: "淡香水正装", capacity: "50ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
      { id: "bn-1002-s3", packageType: "淡香精正装", capacity: "75ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
    ],
  },
  {
    id: "bn-1003",
    brand: "LE LABO",
    name: "Another 13",
    chineseName: "别物13",
    image: "/assets/products/le-labo-another-13.png",
    tags: ["麝香", "木质琥珀", "中性"],
    reason: "贴肤的麝香与干净木质，低调而有辨识度",
    isListed: true,
    hasStock: false,
    saleStatus: "unavailable",
    listedAt: "2026-08-09",
    skus: [
      { id: "bn-1003-s1", packageType: "试香装", capacity: "2ml", stock: "暂缺", availability: "unavailable", saleStatus: "unavailable" },
      { id: "bn-1003-s2", packageType: "旅行装", capacity: "15ml", stock: "暂缺", availability: "unavailable", saleStatus: "unavailable" },
      { id: "bn-1003-s3", packageType: "淡香精正装", capacity: "50ml", stock: "暂缺", availability: "unavailable", saleStatus: "unavailable" },
    ],
  },
  {
    id: "bn-1004",
    brand: "JO MALONE LONDON",
    name: "Wood Sage & Sea Salt",
    chineseName: "鼠尾草与海盐",
    image: "/assets/products/jo-malone-wood-sage.png",
    tags: ["海洋气息", "草本", "送礼"],
    reason: "轻盈海盐与草本木质，接受度高，适合日常与送礼",
    isListed: true,
    hasStock: true,
    saleStatus: "available",
    listedAt: "2026-08-08",
    skus: [
      { id: "bn-1004-s1", packageType: "试香装", capacity: "2ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
      { id: "bn-1004-s2", packageType: "古龙水正装", capacity: "30ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
      { id: "bn-1004-s3", packageType: "古龙水正装", capacity: "100ml", stock: "有货", availability: "in_stock", saleStatus: "available" },
    ],
  },
];

const visibleBrand = (product: Product) => product.brandVerified === false
  ? ""
  : (product.brandNameEn || product.brandNameCn)
    ? [...new Set([product.brandNameEn, product.brandNameCn].filter(Boolean))].join(" ")
    : product.displayBrand || product.brand;
const visibleProductTitle = (product: Product) => [visibleBrand(product), product.name, product.chineseName].filter(Boolean).join(" ");
const fixturesEnabled = import.meta.env.VITE_BEAUTINOW_ENABLE_FIXTURES === "true";
const imagePlaceholder = "/assets/products/image-placeholder.svg";

function ResilientImage({ sources, alt, className }: { sources: Array<string | undefined>; alt: string; className?: string }) {
  const candidateKey = sources.filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).join("\n");
  const candidates = candidateKey ? candidateKey.split("\n") : [];
  const [candidateIndex, setCandidateIndex] = useState(0);
  useEffect(() => setCandidateIndex(0), [candidateKey]);
  const source = candidates[candidateIndex] || imagePlaceholder;
  return (
    <img
      className={className}
      src={source}
      alt={alt}
      draggable={false}
      onError={() => {
        if (candidateIndex < candidates.length) setCandidateIndex((index) => index + 1);
      }}
    />
  );
}

const productImageSources = (product: Product) => [...(product.imageUrls || []), product.image];
const skuImageSources = (sku: Sku, product: Product) => [sku.image, ...(product.imageUrls || []), product.image];
const isStandardPerfume = (product: Product) => product.id.startsWith("pfm_")
  || ["nosetime_source", "official", "confirmed_offer"].includes(product.imageSource || "");
const knownBrandQueries = [
  { query: "Jo Malone", label: "JO MALONE LONDON 祖·玛珑", aliases: ["祖马龙", "祖玛龙", "祖马隆", "祖玛隆", "祖马珑", "祖玛珑", "jomalone"] },
  { query: "Serge Lutens", label: "SERGE LUTENS 芦丹氏", aliases: ["芦丹氏", "卢丹氏", "芦丹市", "鲁丹氏"] },
  { query: "Diptyque", label: "DIPTYQUE 蒂普提克", aliases: ["蒂普提克", "蒂普提克香水", "diptyque"] },
  { query: "Byredo", label: "BYREDO 百瑞德", aliases: ["百瑞德", "拜里朵", "柏芮朵", "byredo"] },
  { query: "Le Labo", label: "LE LABO 实验室香氛", aliases: ["lelabo", "le labo", "实验室香水"] },
  { query: "Tom Ford", label: "TOM FORD 汤姆·福特", aliases: ["汤姆福特", "汤姆·福特", "tf香水"] },
  { query: "Hermes", label: "HERMÈS 爱马仕", aliases: ["爱马仕", "爱玛仕", "hermes"] },
  { query: "Chanel", label: "CHANEL 香奈儿", aliases: ["香奈儿", "香奈尔", "chanel"] },
  { query: "Dior", label: "DIOR 迪奥", aliases: ["迪奥", "dior"] },
  { query: "Guerlain", label: "GUERLAIN 娇兰", aliases: ["娇兰", "姣兰", "guerlain"] },
] as const;

const normalizeQueryText = (value: string) => value.toLowerCase().replace(/[^0-9a-z\u4e00-\u9fff]+/g, "");

function editDistance(left: string, right: string) {
  const rows = Array.from({ length: left.length + 1 }, (_, row) => Array(right.length + 1).fill(0));
  for (let row = 0; row <= left.length; row += 1) rows[row][0] = row;
  for (let column = 0; column <= right.length; column += 1) rows[0][column] = column;
  for (let row = 1; row <= left.length; row += 1) {
    for (let column = 1; column <= right.length; column += 1) {
      rows[row][column] = Math.min(
        rows[row - 1][column] + 1,
        rows[row][column - 1] + 1,
        rows[row - 1][column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1),
      );
    }
  }
  return rows[left.length][right.length];
}

function immediateSearchSuggestions(query: string): SearchSuggestion[] {
  const needle = normalizeQueryText(query);
  if (!needle) return [];
  const brands = knownBrandQueries
    .filter((brand) => {
      const canonical = normalizeQueryText(brand.query);
      if (needle === canonical) return false;
      return brand.aliases.some((alias) => {
        const normalizedAlias = normalizeQueryText(alias);
        return needle === normalizedAlias || (needle.length >= 3 && editDistance(needle, normalizedAlias) <= 1);
      });
    })
    .map<SearchSuggestion>((brand) => ({ query: brand.query, label: brand.label, hint: "常用译名／近似或同音写法", kind: "品牌" }));
  const scentSuggestions = scentFamilies
    .filter((scent) => scent !== "不限香调" && needle !== normalizeQueryText(scent) && editDistance(needle, normalizeQueryText(scent)) <= 1)
    .map<SearchSuggestion>((scent) => ({ query: scent, label: scent, hint: "可能的标准香调名称", kind: "香调" }));
  return [...brands, ...scentSuggestions].slice(0, 5);
}

function fallbackSearchSuggestions(query: string, catalog: Product[]): SearchSuggestion[] {
  const immediate = immediateSearchSuggestions(query);
  const needle = normalizeQueryText(query);
  if (!needle) return immediate;
  const productMatches = catalog
    .map((product) => {
      const text = normalizeQueryText(`${product.brand}${product.name}${product.chineseName}`);
      const common = new Set([...needle].filter((character) => text.includes(character))).size;
      const score = text.includes(needle) ? 100 + needle.length : common / Math.max(1, new Set(needle).size);
      return { product, score };
    })
    .filter((item) => item.score >= 0.5)
    .sort((a, b) => b.score - a.score)
    .slice(0, Math.max(0, 5 - immediate.length))
    .map<SearchSuggestion>(({ product }) => ({
      query: [visibleBrand(product), product.name].filter(Boolean).join(" "),
      label: [visibleBrand(product), product.chineseName || product.name].filter(Boolean).join(" "),
      hint: product.chineseName ? product.name : "可能的商品名称",
      kind: "商品",
    }));
  const seen = new Set<string>();
  return [...immediate, ...productMatches].filter((item) => {
    const key = normalizeQueryText(item.query);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 5);
}

const homepageRecommendationContext: RecommendationContext = {
  purpose: "自己使用",
  details: [],
  answers: {},
  scent: "不限香调",
  budget: "不限预算",
  brand: "不限品牌",
};

function uniqueProducts(products: Product[], limit = 12): Product[] {
  const seen = new Set<string>();
  return products.filter((product) => {
    const key = product.id || normalizeQueryText(`${product.brand} ${product.name} ${product.chineseName}`);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, limit);
}

async function loadHomepageHighlights(seed: Product[]): Promise<Product[]> {
  const products = uniqueProducts(seed.filter((product) => product.recommendable !== false));
  if (products.length >= 12) return products;
  const batches = await Promise.all([0, 1, 2].map((batch) => (
    productSearchApi.recommendations({ context: homepageRecommendationContext, batch, count: 5 })
  )));
  return uniqueProducts([
    ...products,
    ...batches.flatMap((payload) => toProducts(payload.products)),
  ]);
}

function useCatalog() {
  const [catalog, setCatalog] = useState<Product[]>(fixturesEnabled ? fallbackProducts : []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError("");
    productSearchApi.home(12)
      .then(async (payload) => {
        if (disposed) return;
        const directProducts = uniqueProducts(toProducts(payload.products).filter((product) => product.recommendable !== false));
        const products = payload.featuredPolicy === "recommendable_recent_unique"
          ? directProducts
          : await loadHomepageHighlights(directProducts);
        if (disposed) return;
        if (!products.length) throw new ProductSearchApiError("暂时没有已完成资料匹配的香水", "home_highlights_empty", true);
        setCatalog(products);
      })
      .catch((requestError) => {
        if (disposed) return;
        if (!fixturesEnabled) setCatalog([]);
        setError(requestError instanceof Error ? requestError.message : "商品目录暂时不可用");
      })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [revision]);
  return { catalog, loading, error, reload: () => setRevision((value) => value + 1) };
}

function useBrandDirectory() {
  const cached = cachedBrandDirectory();
  const [brands, setBrands] = useState<BrandOption[]>(() => cached?.brands || []);
  const [loading, setLoading] = useState(!cached?.brands.length);
  const [error, setError] = useState("");

  const refresh = useCallback((force = false) => {
    if (!brands.length) setLoading(true);
    setError("");
    return loadBrandDirectory(() => productSearchApi.brands(), force)
      .then((snapshot) => setBrands(snapshot.brands))
      .catch((requestError) => {
        if (!brands.length) setError(requestError instanceof Error ? requestError.message : "品牌目录暂时不可用");
      })
      .finally(() => setLoading(false));
  }, [brands.length]);

  useEffect(() => { void refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { brands, loading, error, reload: () => refresh(true) };
}

export default function Prototype() {
  return <WishlistProvider><PrototypeFlow /></WishlistProvider>;
}

function PrototypeFlow() {
  const [sheet, setSheet] = useState<SheetState>(null);

  const openSheet = useCallback((next: Exclude<SheetState, null>) => setSheet(next), []);
  const initial = useMemo<FlowScreen>(
    () => ({
      id: "home",
      render: (flow) => <HomeScreen flow={flow} openSheet={openSheet} />,
    }),
    [openSheet],
  );

  return (
    <>
      <FlowStack initial={initial} />
      <BottomSheet
        open={Boolean(sheet)}
        onOpenChange={(open) => {
          if (!open) setSheet(null);
        }}
        title={sheet?.title ?? ""}
        description={sheet?.description}
        snap={0.42}
      >
        {sheet?.actionLabel && sheet.actionHref ? <a className="sheet-action" href={sheet.actionHref} target="_blank" rel="noopener noreferrer">{sheet.actionLabel}<ArrowRightIcon /></a> : sheet?.actionLabel ? (
          <button
            className="sheet-action"
            type="button"
            onClick={() => {
              const action = sheet.onAction;
              setSheet(null);
              action?.();
            }}
          >
            {sheet.actionLabel}
            <ArrowRightIcon />
          </button>
        ) : null}
      </BottomSheet>
    </>
  );
}

function HomeScreen({ flow, openSheet }: { flow: FlowControls; openSheet: (sheet: Exclude<SheetState, null>) => void }) {
  const { catalog, loading: catalogLoading, error: catalogError, reload: reloadCatalog } = useCatalog();
  const keyboard = useKeyboard();
  const carouselProducts = useMemo(
    () => [...catalog].filter((product) => product.isListed).slice(0, 6),
    [catalog],
  );
  const [query, setQuery] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchStage, setSearchStage] = useState(0);
  const carouselShellRef = useRef<HTMLDivElement>(null);
  const carouselPausedRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const cameraButtonRef = useRef<HTMLButtonElement>(null);
  const [captureOpen,setCaptureOpen]=useState(false);
  const [captureBusy,setCaptureBusy]=useState(false);
  const [captureError,setCaptureError]=useState("");
  const [capturePreview,setCapturePreview]=useState("");
  const [captureImage,setCaptureImage]=useState("");
  const [captureFile,setCaptureFile]=useState<File|null>(null);
  const recognitionControllerRef=useRef<AbortController|null>(null);
  const homeActive = useRef(true);
  useEffect(()=>{homeActive.current=true;return()=>{homeActive.current=false;recognitionControllerRef.current?.abort();};},[]);
  const searchPendingRef = useRef(false);
  useEffect(()=>()=>{if(capturePreview)URL.revokeObjectURL(capturePreview);},[capturePreview]);
  const closeCapture=useCallback(()=>{if(!captureBusy){setCaptureOpen(false);setCaptureError("");setCapturePreview("");setCaptureImage("");setCaptureFile(null);}},[captureBusy]);
  const runImageRecognition=useCallback(async(image:string)=>{
    if(!image||searchPendingRef.current)return;
    searchPendingRef.current=true;setCaptureOpen(false);setSearching(true);setSearchStage(0);
    const controller=new AbortController();recognitionControllerRef.current=controller;
    try {
      const result=await productSearchApi.aiFind({text:'',imageDataUrl:image},controller.signal);
      if(!homeActive.current)return;
      const rows=flattenAIFindProducts(result);
      const products=toProducts(rows);
      const source=result.searchPage?.query || cleanAIFindQuery(result.displayQuery || result.query);
      setCaptureError("");setCapturePreview("");setCaptureImage("");setCaptureFile(null);
      flow.push(makeCollectionSearchResultsScreen(result.searchPage?(result.searchPage.confirmBrand?'brand':'keyword'):'image',result.searchPage?source:imageResultTitle(result,products.length),products,source,catalog,openSheet,result.searchPage||undefined,{...result,products:rows}));
    } catch(error) {
      if(!homeActive.current||(error as {code?:string})?.code==='aborted')return;
      setCaptureError(aiErrorCopy(error));setCapturePreview(image);setCaptureOpen(true);
    } finally {
      searchPendingRef.current=false;
      if(homeActive.current){setSearching(false);setCaptureBusy(false);}
    }
  },[catalog,flow,openSheet]);
  const selectCapture=useCallback(async(file:File)=>{
    if(searchPendingRef.current)return;
    setCaptureBusy(true);setCaptureError("");setCaptureImage("");setCaptureFile(file);keyboard.hide();
    setCapturePreview(URL.createObjectURL(file));
    try {
      const image=await readImageFile(file);
      if(!homeActive.current)return;
      setCaptureImage(image);setCapturePreview(image);
      await runImageRecognition(image);
    } catch(error) {
      if(homeActive.current)setCaptureError(aiErrorCopy(error));
    } finally {if(homeActive.current)setCaptureBusy(false);}
  },[keyboard,runImageRecognition]);

  const pushResult = useCallback(
    (product: Product, source = query || product.name) => flow.push(makeProductDetailScreen(
      product,
      { kind: "search", label: `搜索“${source}”` },
      catalog,
      openSheet,
    )),
    [catalog, flow, openSheet, query],
  );

  const runSearch = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!query.trim() || searchPendingRef.current) return;
    keyboard.hide();
    const immediateSuggestions = fixturesEnabled ? immediateSearchSuggestions(query) : [];
    if (immediateSuggestions.length) {
      keyboard.hide();
      flow.push(makeSearchClarificationScreen(query, immediateSuggestions, catalog, openSheet));
      return;
    }
    searchPendingRef.current = true;
    const revealTimer = window.setTimeout(() => {
      keyboard.hide();
      setSearchStage(0);
      setSearching(true);
    }, 320);
    let resolution: SearchResolution | null = null;
    let requestError = "";
    try {
      resolution = await findProductFromCatalog(query);
    } catch (error) {
      resolution = null;
      requestError = error instanceof Error ? error.message : "搜索暂时不可用";
    } finally {
      window.clearTimeout(revealTimer);
      if(homeActive.current)setSearching(false);
      searchPendingRef.current = false;
    }
    if(!homeActive.current)return;
    if (!resolution) {
      if (fixturesEnabled) {
        flow.push(makeSearchClarificationScreen(query, fallbackSearchSuggestions(query, catalog), catalog, openSheet));
      } else {
        openSheet({ title: "搜索暂时不可用", description: requestError || "请检查网络后稍后重试，系统不会使用演示商品代替真实结果。" });
      }
      return;
    }
    if (resolution.mode === "clarify") {
      flow.push(makeSearchClarificationScreen(query, resolution.suggestions, catalog, openSheet));
      return;
    }
    if (resolution.mode === "brand") {
      flow.push(makeCollectionSearchResultsScreen("brand", resolution.brand, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "category") {
      flow.push(makeCollectionSearchResultsScreen("category", resolution.category, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "promotion") {
      flow.push(makeCollectionSearchResultsScreen("promotion", resolution.collection, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "keyword") {
      flow.push(makeCollectionSearchResultsScreen("keyword", resolution.keyword, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    pushResult(resolution.product, query);
  };

  useEffect(() => {
    if (!searching) return;
    const timer = window.setInterval(() => setSearchStage((value) => (value + 1) % 3), 850);
    return () => window.clearInterval(timer);
  }, [searching]);

  useEffect(() => {
    const node = carouselShellRef.current?.querySelector<HTMLElement>(".latest-carousel");
    if (!node) return;
    const syncIndex = () => setActiveSlide(Math.round(node.scrollLeft / Math.max(1, node.clientWidth)));
    const pauseCarousel = () => { carouselPausedRef.current = true; };
    node.addEventListener("scroll", syncIndex, { passive: true });
    node.addEventListener("pointerdown", pauseCarousel, { passive: true });
    const timer = window.setInterval(() => {
      if (carouselPausedRef.current) return;
      const current = Math.round(node.scrollLeft / Math.max(1, node.clientWidth));
      const next = (current + 1) % Math.max(1, carouselProducts.length);
      node.scrollTo({ left: next * node.clientWidth, behavior: next === 0 ? "auto" : "smooth" });
      setActiveSlide(next);
    }, 4500);
    return () => {
      node.removeEventListener("scroll", syncIndex);
      node.removeEventListener("pointerdown", pauseCarousel);
      window.clearInterval(timer);
    };
  }, [carouselProducts.length]);

  return (
    <>
    <MobileScroll className="app-screen">
      <main className="home-content" data-testid="home-screen">
        <div className="home-title-row"><h1 className="editorial-headline">循着记忆，<br />找到那支香</h1><WishlistEntry onOpen={() => flow.push(makeWishlistScreen())} /></div>

        <section className="latest-section" aria-label="精选香水" ref={carouselShellRef}>
          {catalogError && !catalog.length ? <div className="catalog-error" role="status"><strong>商品目录暂时不可用</strong><span>{catalogError}</span><button type="button" onClick={reloadCatalog}>重新加载</button></div> : null}
          <Carousel className="latest-carousel" contentClassName="latest-track" ariaLabel="精选香水轮播">
            {carouselProducts.map((product, index) => (
              <button
                className="latest-card"
                type="button"
                key={product.id}
                onClick={() => flow.push(makeProductDetailScreen(
                  product,
                  { kind: "featured", label: "来自首页精选" },
                  catalog,
                  openSheet,
                ))}
                aria-label={`查看 ${visibleProductTitle(product)} 香水资料`}
              >
                <ResilientImage sources={productImageSources(product)} alt={visibleProductTitle(product)} />
                <span className="latest-copy">
                  <span className="latest-meta">精选香水&nbsp;&nbsp;{String(index + 1).padStart(2, "0")} / {String(carouselProducts.length).padStart(2, "0")}</span>
                  {visibleBrand(product) ? <strong>{visibleBrand(product)}</strong> : null}
                  <span className="latest-name"><span>{product.name}</span>{product.chineseName ? <small>{product.chineseName}</small> : null}</span>
                  <span className="latest-action">查看香水资料 <ArrowRightIcon /></span>
                </span>
              </button>
            ))}
          </Carousel>
          <div className="carousel-dots" aria-label="选择精选香水">
            {carouselProducts.map((product, index) => (
              <button
                key={product.id}
                type="button"
                data-active={index === activeSlide ? "true" : "false"}
                aria-label={`查看第 ${index + 1} 款精选香水`}
                onClick={() => {
                  carouselPausedRef.current = true;
                  const node = carouselShellRef.current?.querySelector<HTMLElement>(".latest-carousel");
                  node?.scrollTo({ left: index * node.clientWidth, behavior: "smooth" });
                  setActiveSlide(index);
                }}
              />
            ))}
          </div>
        </section>

        <form
          className="search-bar home-search with-camera"
          onSubmit={runSearch}
          onClick={(event) => {
            if (event.target === event.currentTarget || (event.target instanceof Element && event.target.closest(".search-submit"))) searchInputRef.current?.focus();
          }}
        >
          <span className="search-submit" aria-hidden="true"><MagnifyingGlassIcon /></span>
          <KeyboardInput
            ref={searchInputRef}
            data-testid="search-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onCompositionStart={(event)=>{event.currentTarget.dataset.composing="true";}}
            onCompositionEnd={(event)=>{delete event.currentTarget.dataset.composing;}}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing && event.currentTarget.dataset.composing!=="true") {
                event.preventDefault();
                if (query.trim()) void runSearch();
              }
            }}
            placeholder="搜索品牌或香水名"
            aria-label="直接搜索香水"
          />
          <button ref={cameraButtonRef} className="search-camera" type="button" aria-label="拍照识别香水" aria-haspopup="dialog" disabled={searching} onClick={(event)=>{event.stopPropagation();keyboard.hide();setCaptureError("");setCaptureOpen(true);}}><CameraIcon /></button>
          <button className="search-action" type="submit" aria-label="开始搜索">搜索</button>
        </form>

        <button className="recommend-link" type="button" onClick={() => flow.push(makeRecommendationScreen(catalog, openSheet))}>
          <span><strong>不知道选哪款香水？</strong><small>告诉我们使用场景、喜欢的气味或预算，也可以跟着问题一步步选。</small><b>开始选香 <ArrowRightIcon /></b></span>
        </button>
        <button className="privacy-link" type="button" onClick={openPrivacyPolicy}>隐私政策</button>
      </main>
    </MobileScroll>
    {searching || catalogLoading ? <SearchTransitionView stage={searchStage}/> : null}
    <ImageCaptureGuide open={captureOpen} busy={captureBusy} error={captureError} previewUrl={capturePreview} onClose={closeCapture} onRetry={captureImage?()=>void runImageRecognition(captureImage):captureFile?()=>void selectCapture(captureFile):undefined} onSelect={selectCapture} returnFocus={cameraButtonRef.current}/>
    </>
  );
}

function InlineBack({ flow }: { flow: FlowControls }) {
  return <button className="inline-back" type="button" onClick={flow.pop}>← 返回上一页</button>;
}

function SearchTransitionView({ stage }: { stage: number }) {
  return (
    <div className="search-transition" role="status" aria-live="polite" aria-label="正在搜索">
      <img src="/assets/brand/beautinow-logo-cropped.png" alt="Beautinow" draggable={false} />
      <div className="search-orbit" aria-hidden="true"><MagnifyingGlassIcon /><span /><span /><span /></div>
      <p>Beautinow 专业搜索</p>
      <h2>正在循着线索，寻找那支香</h2>
      <small>{["正在理解你的搜索…", "正在核对收录信息…", "正在整理最接近的结果…"][stage]}</small>
    </div>
  );
}

function ResultSearchBar({
  flow,
  initialValue,
  catalog,
  openSheet,
}: {
  flow: FlowControls;
  initialValue: string;
  catalog: Product[];
  openSheet: (sheet: Exclude<SheetState, null>) => void;
}) {
  const keyboard = useKeyboard();
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef(false);
  const screenActiveRef=useRef(true);
  useEffect(()=>{screenActiveRef.current=true;return()=>{screenActiveRef.current=false;};},[]);
  const [query, setQuery] = useState(initialValue);
  const [searching, setSearching] = useState(false);
  const [searchStage, setSearchStage] = useState(0);

  const runSearch = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!query.trim() || pendingRef.current) return;
    keyboard.hide();
    const immediateSuggestions = fixturesEnabled ? immediateSearchSuggestions(query) : [];
    if (immediateSuggestions.length) {
      keyboard.hide();
      flow.push(makeSearchClarificationScreen(query, immediateSuggestions, catalog, openSheet));
      return;
    }
    pendingRef.current = true;
    const revealTimer = window.setTimeout(() => {
      keyboard.hide();
      setSearchStage(0);
      setSearching(true);
    }, 320);
    let resolution: SearchResolution | null = null;
    let requestError = "";
    try {
      resolution = await findProductFromCatalog(query);
    } catch (error) {
      resolution = null;
      requestError = error instanceof Error ? error.message : "搜索暂时不可用";
    } finally {
      window.clearTimeout(revealTimer);
      if(screenActiveRef.current)setSearching(false);
      pendingRef.current = false;
    }
    if(!screenActiveRef.current)return;
    if (!resolution) {
      if (fixturesEnabled) {
        flow.push(makeSearchClarificationScreen(query, fallbackSearchSuggestions(query, catalog), catalog, openSheet));
      } else {
        openSheet({ title: "搜索暂时不可用", description: requestError || "请检查网络后稍后重试。" });
      }
      return;
    }
    if (resolution.mode === "clarify") {
      flow.push(makeSearchClarificationScreen(query, resolution.suggestions, catalog, openSheet));
      return;
    }
    if (resolution.mode === "brand") {
      flow.push(makeCollectionSearchResultsScreen("brand", resolution.brand, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "category") {
      flow.push(makeCollectionSearchResultsScreen("category", resolution.category, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "promotion") {
      flow.push(makeCollectionSearchResultsScreen("promotion", resolution.collection, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "keyword") {
      flow.push(makeCollectionSearchResultsScreen("keyword", resolution.keyword, resolution.products, query, catalog, openSheet, resolution.page));
      return;
    }
    flow.push(makeProductDetailScreen(
      resolution.product,
      { kind: "search", label: `搜索“${query}”` },
      catalog,
      openSheet,
    ));
  };

  useEffect(() => {
    if (!searching) return;
    const timer = window.setInterval(() => setSearchStage((value) => (value + 1) % 3), 850);
    return () => window.clearInterval(timer);
  }, [searching]);

  return (
    <>
      <form
        className="result-query result-query-form"
        onSubmit={runSearch}
        onClick={(event) => {
          if (!(event.target instanceof Element) || !event.target.closest("button")) inputRef.current?.focus();
        }}
      >
        <button type="submit" aria-label="搜索"><MagnifyingGlassIcon /></button>
        <KeyboardInput
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (query.trim()) void runSearch();
            }
          }}
          aria-label="继续搜索香水"
          placeholder="继续搜索香水"
        />
        <button
          type="button"
          aria-label="清空搜索"
          onClick={() => {
            setQuery("");
            window.requestAnimationFrame(() => inputRef.current?.focus());
          }}
        ><Cross2Icon /></button>
      </form>
      {searching ? <SearchTransitionView stage={searchStage} /> : null}
    </>
  );
}

function makeSearchClarificationScreen(
  source: string,
  suggestions: SearchSuggestion[],
  catalog: Product[],
  openSheet: (sheet: Exclude<SheetState, null>) => void,
): FlowScreen {
  return {
    id: `clarify-${normalizeQueryText(source) || "search"}`,
    render: (flow) => (
      <SearchClarificationScreen
        flow={flow}
        source={source}
        suggestions={suggestions}
        catalog={catalog}
        openSheet={openSheet}
      />
    ),
  };
}

function SearchClarificationScreen({
  flow,
  source,
  suggestions,
  catalog,
  openSheet,
}: {
  flow: FlowControls;
  source: string;
  suggestions: SearchSuggestion[];
  catalog: Product[];
  openSheet: (sheet: Exclude<SheetState, null>) => void;
}) {
  const keyboard = useKeyboard();
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef(false);
  const screenActiveRef=useRef(true);
  useEffect(()=>{screenActiveRef.current=true;return()=>{screenActiveRef.current=false;};},[]);
  const [query, setQuery] = useState(source);
  const [searching, setSearching] = useState(false);
  const [searchStage, setSearchStage] = useState(0);
  const [error, setError] = useState("");
  const isBrandConfirmation = suggestions.some((suggestion) => suggestion.kind === "品牌");

  const runConfirmedSearch = async (confirmedQuery: string, options?: { confirmBrand?: boolean; literal?: boolean }) => {
    if (pendingRef.current) return;
    keyboard.hide();
    pendingRef.current = true;
    setError("");
    const revealTimer = window.setTimeout(() => {
      keyboard.hide();
      setSearchStage(0);
      setSearching(true);
    }, 320);
    let resolution: SearchResolution | null = null;
    try {
      resolution = await findProductFromCatalog(confirmedQuery, options);
    } catch (requestError) {
      resolution = null;
      setError(requestError instanceof Error ? requestError.message : "商品信息仍在同步，请稍后再试。");
    } finally {
      window.clearTimeout(revealTimer);
      if(screenActiveRef.current)setSearching(false);
      pendingRef.current = false;
    }
    if(!screenActiveRef.current)return;
    if (!resolution) {
      setError((current) => current || "商品信息仍在同步，请稍候再选择一次。你也可以返回修改搜索词。");
      return;
    }
    if (resolution.mode === "clarify") {
      flow.push(makeSearchClarificationScreen(confirmedQuery, resolution.suggestions, catalog, openSheet));
      return;
    }
    if (resolution.mode === "brand") {
      flow.push(makeCollectionSearchResultsScreen("brand", resolution.brand, resolution.products, confirmedQuery, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "category") {
      flow.push(makeCollectionSearchResultsScreen("category", resolution.category, resolution.products, confirmedQuery, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "promotion") {
      flow.push(makeCollectionSearchResultsScreen("promotion", resolution.collection, resolution.products, confirmedQuery, catalog, openSheet, resolution.page));
      return;
    }
    if (resolution.mode === "keyword") {
      flow.push(makeCollectionSearchResultsScreen("keyword", resolution.keyword, resolution.products, confirmedQuery, catalog, openSheet, resolution.page));
      return;
    }
    flow.push(makeProductDetailScreen(
      resolution.product,
      { kind: "search", label: `搜索“${confirmedQuery}”` },
      catalog,
      openSheet,
    ));
  };

  useEffect(() => {
    if (!searching) return;
    const timer = window.setInterval(() => setSearchStage((value) => (value + 1) % 3), 850);
    return () => window.clearInterval(timer);
  }, [searching]);

  return (
    <MobileScroll className="app-screen">
      <main className="clarification-content" data-testid="search-clarification">
        <InlineBack flow={flow} />
        <h1>{isBrandConfirmation ? "请选择你要找的品牌" : "先确认名称，再为你查找"}</h1>
        <p className="section-intro">{isBrandConfirmation ? `“${source}”可能对应以下品牌。确认后，将展示该品牌的全部收录香水。` : `“${source}”可能是常用译名、近似词或同音写法。选择更准确的名称后，再查询完整收录商品。`}</p>
        <form
          className="clarification-query"
          onSubmit={(event) => {
            event.preventDefault();
            if (query.trim()) void runConfirmedSearch(query.trim());
          }}
          onClick={(event) => {
            if (!(event.target instanceof Element) || !event.target.closest("button")) inputRef.current?.focus();
          }}
        >
          <button type="submit" aria-label="搜索修改后的名称"><MagnifyingGlassIcon /></button>
          <KeyboardInput
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (query.trim()) void runConfirmedSearch(query.trim());
              }
            }}
            aria-label="修改搜索词"
            placeholder="修改搜索词"
          />
          <button
            type="button"
            aria-label="清空搜索词"
            onClick={() => {
              setQuery("");
              window.requestAnimationFrame(() => inputRef.current?.focus());
            }}
          ><Cross2Icon /></button>
        </form>
        {suggestions.length ? (
          <section className="suggestion-list" aria-label="可能想找">
            <p>你可能想找</p>
            {suggestions.map((suggestion) => (
              <button type="button" key={`${suggestion.kind}-${suggestion.query}`} onClick={() => void runConfirmedSearch(suggestion.query, { confirmBrand: suggestion.kind === "品牌" })}>
                <span className="suggestion-kind">{suggestion.kind}</span>
                <span><strong>{suggestion.label}</strong><small>{['phonetic','phonetic_prefix'].includes(suggestion.matchType||'')?'按读音找到 · ':''}{suggestion.hint}</small></span>
                <ArrowRightIcon />
              </button>
            ))}
          </section>
        ) : (
          <p className="clarification-empty">暂时没有足够接近的标准名称，你可以按原词继续查找，或返回修改搜索词。</p>
        )}
        {error ? <p className="clarification-error" role="status">{error}</p> : null}
        <button className="original-search-button" type="button" onClick={() => void runConfirmedSearch(source, { literal: true })}>
          {isBrandConfirmation ? `以上都不是，按品名搜索“${source}”` : `仍按“${source}”查找`} <ArrowRightIcon />
        </button>
      </main>
      {searching ? <SearchTransitionView stage={searchStage} /> : null}
    </MobileScroll>
  );
}

function makeProductDetailScreen(
  product: Product,
  source: ProductDetailSource,
  catalog: Product[],
  openSheet: (sheet: Exclude<SheetState, null>) => void,
): FlowScreen {
  return {
    id: `product-${product.id}`,
    render: (flow) => <ProductDetailScreen flow={flow} product={product} source={source} catalog={catalog} openSheet={openSheet} />,
  };
}

function useProductDetail(product: Product) {
  const [detail, setDetail] = useState<Product | null>(fixturesEnabled ? product : null);
  const [loading, setLoading] = useState(!fixturesEnabled);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let disposed = false;
    if (fixturesEnabled) {
      setDetail(product);
      setLoading(false);
      setError("");
      return () => { disposed = true; };
    }
    setDetail(null);
    setLoading(true);
    setError("");
    productSearchApi.product(product.id)
      .then((payload) => {
        if (!disposed) setDetail(toProduct(payload));
      })
      .catch((requestError) => {
        if (disposed) return;
        setError(requestError instanceof Error ? requestError.message : "商品详情暂时无法加载");
      })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [product, revision]);
  return { detail, loading, error, reload: () => setRevision((value) => value + 1) };
}

function ExpandableText({ text, id, className, expandLabel = "展开内容", collapseLabel = "收起内容" }: {
  text: string; id: string; className: string; expandLabel?: string; collapseLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const paragraph = paragraphRef.current;
    if (!paragraph) return;
    const measure = () => { if (!expanded) setOverflows(paragraph.scrollHeight > paragraph.clientHeight + 1); };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(paragraph);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [text, expanded]);
  return <>
    <p ref={paragraphRef} id={id} className={`${className}${expanded ? "" : " line-clamp-three"}`}>{text}</p>
    {overflows || expanded ? <button className="detail-text-action" type="button" aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(!expanded)}>{expanded ? collapseLabel : expandLabel}</button> : null}
  </>;
}

function ProductReviewSection({ product }: { product: Product }) {
  const insights = product.reviewInsights;
  const [showMore, setShowMore] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  if (!insights?.hasDirectReviews || !insights.reviews.length) return null;
  const reviews = (insights.reviewFeed?.length ? insights.reviewFeed : insights.reviews).slice(0, 15);
  const visible = showMore ? reviews : reviews.slice(0, 2);
  return <section ref={sectionRef} id={`perfume-reviews-${product.id}`} className="product-reviews">
    <h2>香评</h2>
    <div className="rating-overview">
      {hasReviewRating(insights) ? <div className="rating-value">{insights.rating!.toFixed(1)}<small> / 5</small></div> : null}
      <div>{hasReviewRating(insights) ? <p>{insights.ratingCount.toLocaleString()} 次评分</p> : null}<p>{reviewCountLabel(insights.directReviewCount, reviews.length)}</p></div>
    </div>
    <div className="review-list">{visible.map((review, index) => {
      const content = review.content || review.excerpt || "";
      return <article key={index}>
        <div className="review-meta"><span>{review.score != null ? `${review.score.toFixed(1)} / 5` : ""}</span>{review.likes > 0 ? <span>{review.likes} 人赞同</span> : null}</div>
        <ExpandableText text={content} id={`review-content-${product.id}-${index}`} className="review-body" />
      </article>;
    })}</div>
    {reviews.length > 2 ? <button className="review-toggle" type="button" aria-expanded={showMore} onClick={() => { setShowMore(!showMore); if (showMore) requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ block: "start" })); }}>{showMore ? "收起更多香评" : `查看精选香评（${reviews.length}）`} <ArrowRightIcon /></button> : null}

  </section>;
}

function ProductDescription({ product }: { product: Product }) {
  const description = [...new Set((product.description || "").split(/\n+/).map(text => text.trim()).filter(Boolean))].join("\n\n");
  if (!description) return null;
  return <section className="product-description"><h2>香水介绍</h2>
    <ExpandableText text={description} id={`description-${product.id}`} className="description-copy" expandLabel="展开介绍" collapseLabel="收起介绍" />
  </section>;
}

function ProductResultRow({
  product,
  onSelect,
}: {
  product: Product;
  onSelect: () => void;
}) {
  const stockCopy=productSaleCopy(product);
  const stockConfirmed=stockCopy==='有货'||stockCopy==='暂时无货'||stockCopy==='已下架';
  return (
    <button className="product-result-row" type="button" onClick={onSelect} aria-label={`查看 ${visibleProductTitle(product)}`}>
      <ResilientImage sources={productImageSources(product)} alt={visibleProductTitle(product)} />
      <span className="recommend-info">
        {visibleBrand(product) ? <strong>{visibleBrand(product)}</strong> : null}
        <b>{product.name} {product.chineseName}</b>
        {stockConfirmed?<small className={`stock-indicator ${stockCopy==='有货'?'':'stock-empty'}`}><i />{stockCopy}</small>:<small className="result-action">{stockCopy}</small>}
      </span>
      <span className="result-row-action">{productAction(product)} <ArrowRightIcon /></span>
    </button>
  );
}

function ProductGrid({products,onSelect}:{products:Product[];onSelect:(product:Product)=>void}) {
  return <div className="related-product-grid">{uniqueProducts(products,Number.MAX_SAFE_INTEGER).map(product=>{
    const versions=[...new Set(product.skus.map(s=>s.concentration||s.packageType).filter(Boolean))];
    const stockCopy=productSaleCopy(product);const stockConfirmed=stockCopy==='有货'||stockCopy==='暂时无货'||stockCopy==='已下架';
    return <button className="related-product-card" type="button" key={product.id} onClick={()=>onSelect(product)} aria-label={`查看 ${visibleProductTitle(product)}`}>
      <ResilientImage sources={productImageSources(product)} alt={visibleProductTitle(product)}/>
      <span className="grid-product-brand">{visibleBrand(product)}</span>
      <strong className="grid-product-name">{product.chineseName||product.name}</strong>
      <small className="grid-product-version">{versions.join(' / ')}</small>
      <span className="grid-product-footer">{stockConfirmed?<small className={`stock-indicator ${stockCopy==='有货'?'':'stock-empty'}`}><i/>{stockCopy}</small>:<small>{stockCopy}</small>}<span>{productAction(product)} <ArrowRightIcon/></span></span>
    </button>;
  })}</div>;
}

function RelatedProducts({reference,initial,initialStatus,initialPoolToken,context={},onSelect,onAll}:{reference:Product;initial?:Product[];initialStatus?:AIFindResponse['relatedStatus'];initialPoolToken?:string;context?:RelatedContext;onSelect:(product:Product)=>void;onAll:(token?:string)=>void}) {
  const [poolToken,setPoolToken]=useState(initialPoolToken);
  const contextKey=JSON.stringify(context);
  const [products,setProducts]=useState<Product[]>(initial||[]);
  const [loading,setLoading]=useState(initial===undefined);
  const [error,setError]=useState(initialStatus?.state==='error'?(initialStatus.message||'相关商品暂时无法加载，请稍后重试'):'');
  const [retry,setRetry]=useState(0);
  const [shouldFetch,setShouldFetch]=useState(initial===undefined);
  useEffect(()=>{
    if(!shouldFetch)return;
    let active=true;setLoading(true);setError('');
    productSearchApi.related(reference.id,context).then(result=>{if(active){setProducts(toProducts(result.products));setPoolToken(result.poolToken);setShouldFetch(false);}}).catch(err=>{if(active){setError(err.message);setShouldFetch(false);}}).finally(()=>{if(active)setLoading(false);});
    return ()=>{active=false;};
  },[reference.id,retry,shouldFetch,contextKey]);
  if(!loading&&!error&&!products.length)return null;
  return <section className="related-product-results" aria-labelledby="related-product-results-title">
    <h2 id="related-product-results-title">相关商品</h2>
    {loading?<p className="section-intro" role="status">正在查找相关香水…</p>:error?<div className="catalog-error" role="alert"><span>{error}</span><button type="button" onClick={()=>{setShouldFetch(true);setRetry(v=>v+1);}}>重新加载</button></div>:<>
      <ProductGrid products={products} onSelect={onSelect}/>
      <button className="view-all-related" type="button" onClick={()=>onAll(poolToken)}>查看全部相似商品 <ArrowRightIcon/></button>
    </>}
  </section>;
}

function makeSimilarProductsScreen(reference:Product,catalog:Product[],openSheet:(sheet:Exclude<SheetState,null>)=>void,context:RelatedContext={},poolToken=''):FlowScreen {
  return {id:`similar-${reference.id}`,render:flow=><SimilarProductsScreen flow={flow} reference={reference} catalog={catalog} openSheet={openSheet} context={context} initialPoolToken={poolToken}/>};
}

type SimilarFilters={brands:string[];scents:string[];priceRanges:string[]};
function SimilarProductsScreen({flow,reference,catalog,openSheet,context={},initialPoolToken=''}:{flow:FlowControls;reference:Product;catalog:Product[];openSheet:(sheet:Exclude<SheetState,null>)=>void;context?:RelatedContext;initialPoolToken?:string}) {
  const [poolToken,setPoolToken]=useState(initialPoolToken);
  const [filters,setFilters]=useState<SimilarFilters>({brands:[],scents:[],priceRanges:[]});
  const [options,setOptions]=useState<SimilarResponse['filters']>({brands:[],scents:[],priceRanges:[]});
  const [open,setOpen]=useState<keyof SimilarFilters|null>(null);
  const [activeFilter,setActiveFilter]=useState<keyof SimilarFilters>('brands');
  const optionsList=useRef<HTMLDivElement>(null);
  const [query,setQuery]=useState('');
  const [products,setProducts]=useState<Product[]>([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [total,setTotal]=useState(0);
  const [offset,setOffset]=useState(0);
  const [hasMore,setHasMore]=useState(false);
  const [retry,setRetry]=useState(0);
  const sequence=useRef(0);
  const toolbar=useRef<HTMLDivElement>(null);
  async function load(start:number,seq:number) {
    setLoading(true);setError('');
    try {
      const result=await productSearchApi.similar(reference.id,filters,start,context,poolToken);
      if(seq!==sequence.current)return;
      const next=toProducts(result.products);
      setProducts(current=>start?uniqueProducts([...current,...next],Number.MAX_SAFE_INTEGER):next);
      if(result.poolToken)setPoolToken(result.poolToken);
      setOptions(result.filters);setTotal(result.total);setOffset(result.nextOffset);setHasMore(result.hasMore);
    } catch(err){if(seq===sequence.current)setError(err instanceof Error?err.message:'暂时无法加载');}
    finally {if(seq===sequence.current)setLoading(false);}
  }
  useEffect(()=>{const seq=++sequence.current;setProducts([]);void load(0,seq);return()=>{sequence.current++;};},[reference.id,filters,retry]);
  useEffect(()=>{
    const dismiss=(event:PointerEvent)=>{if(toolbar.current&&!toolbar.current.contains(event.target as Node))setOpen(null);};
    document.addEventListener('pointerdown',dismiss);return()=>document.removeEventListener('pointerdown',dismiss);
  },[]);
  function toggle(group:keyof SimilarFilters,value:string){setFilters(current=>({...current,[group]:current[group].includes(value)?current[group].filter(v=>v!==value):[...current[group],value]}));window.requestAnimationFrame(()=>{if(optionsList.current)optionsList.current.scrollTop=0;});}
  const labels:Record<keyof SimilarFilters,string>={brands:'品牌',priceRanges:'价格',scents:'香调'};
  const choices=open==='priceRanges'?options.priceRanges.map(r=>({value:r.key,label:r.label})):(open?options[open]:[]).map(value=>({value,label:value}));
  return <><MobileScroll className="app-screen"><main className="result-content brand-search-results similar-products-screen" data-testid="similar-products-screen">
    <InlineBack flow={flow}/><h1>相似商品</h1>
    <div className="similar-filter-toolbar" ref={toolbar} onKeyDown={event=>{if(event.key==='Escape')setOpen(null);}}>
      <div className="similar-filter-buttons">{(['brands','priceRanges','scents'] as const).map(key=><button key={key} type="button" data-active={activeFilter===key} aria-expanded={open===key} aria-controls="similar-filter-menu" onClick={()=>{setActiveFilter(key);setOpen(open===key?null:key);setQuery('');}}>{labels[key]}{filters[key].length?` (${filters[key].length})`:''}<span aria-hidden="true">⌄</span></button>)}</div>
      {open?<div id="similar-filter-menu" className="similar-filter-menu" role="group" aria-label={`${labels[open]}筛选`}>
        {open!=='priceRanges'?<input aria-label={`搜索${labels[open]}`} placeholder={`搜索${labels[open]}`} value={query} onChange={event=>setQuery(event.target.value)}/>:null}
        <div className="similar-filter-options" ref={optionsList}>{orderedFilterOptions(choices,filters[open],query).map(choice=><label key={choice.value}><input type="checkbox" checked={filters[open].includes(choice.value)} onChange={()=>toggle(open,choice.value)}/><span>{choice.label}</span></label>)}{!choices.length?<p>暂无可选项</p>:null}</div>
        <div className="similar-filter-actions"><button type="button" onClick={()=>setFilters(current=>({...current,[open]:[]}))}>清空</button><button type="button" onClick={()=>setOpen(null)}>收起</button></div>
      </div>:null}
    </div>
    <div className="similar-result-summary"><span>{loading?'正在查找…':`${total} 款香水`}</span>{Object.values(filters).some(v=>v.length)?<button type="button" onClick={()=>setFilters({brands:[],scents:[],priceRanges:[]})}>清空筛选</button>:null}</div>
    {error?<div className="catalog-error" role="alert"><span>{error}</span><button type="button" onClick={()=>setRetry(v=>v+1)}>重试</button></div>:null}
    {!loading&&!error&&!products.length?<p className="section-intro">没有符合当前筛选的香水，可以清空筛选重新查看。</p>:null}
    <ProductGrid products={products} onSelect={product=>flow.push(makeProductDetailScreen(product,{kind:'recommendation',label:'相似商品'},catalog,openSheet))}/>
    {loading&&products.length?<p className="section-intro" role="status">正在加载香水…</p>:!loading&&hasMore?<button className="view-all-related" type="button" onClick={()=>void load(offset,++sequence.current)}>加载更多 <ArrowRightIcon/></button>:null}
  </main></MobileScroll>{loading&&!products.length?<SearchTransitionView stage={1}/>:null}</>;
}

function ProductScentProfile({ product }: { product: Product }) {
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const scentFamily = product.fragranceType || (product.sourceOnly ? "香调资料未收录" : "");
  const allTags = [...new Set(product.tags.filter((tag) => tag !== scentFamily))];
  const scentTags = tagsExpanded ? allTags : allTags.slice(0, 6);
  const noteGroups = [
    { key: "top" as const, label: "前调", notes: product.notePyramid?.top || [] },
    { key: "middle" as const, label: "中调", notes: product.notePyramid?.middle || [] },
    { key: "base" as const, label: "后调", notes: product.notePyramid?.base || [] },
  ].filter((group) => group.notes.length);
  // Keep the standard three rows on a source-only detail when some note roles
  // are missing. The empty rows explicitly say so; no notes are fabricated.
  const groups = product.sourceOnly && (noteGroups.length || !product.scentNotes?.length) ? [
    { key: "top" as const, label: "前调", notes: product.notePyramid?.top || [] },
    { key: "middle" as const, label: "中调", notes: product.notePyramid?.middle || [] },
    { key: "base" as const, label: "后调", notes: product.notePyramid?.base || [] },
  ] : noteGroups;
  const hasProfile = Boolean(scentFamily || scentTags.length || groups.length || product.scentNotes?.length);

  if (!hasProfile) return product.reason ? <p className="purchase-reason">{product.reason.includes("仅用于商品搜索") ? "香气资料暂未补全，可先查看商品规格。" : product.reason}</p> : null;

  return (
    <section className="scent-profile" aria-labelledby={`scent-profile-${product.id}`}>
      <h2 id={`scent-profile-${product.id}`}>香气构成</h2>
      <div className="scent-lead">
        {scentFamily ? <strong>{scentFamily}</strong> : null}
        {scentTags.length ? <div className="scent-tags">{scentTags.map(tag => <span key={tag}>{tag}</span>)}</div> : null}
        {allTags.length > 6 ? <button className="detail-text-action" type="button" aria-expanded={tagsExpanded} onClick={() => setTagsExpanded(!tagsExpanded)}>{tagsExpanded ? "收起标签" : "展开全部标签"}</button> : null}
      </div>
      {!groups.length && product.scentNotes?.length ? <div className="note-pyramid"><div className="note-row"><strong>气味</strong><p>{product.scentNotes.join(" · ")}</p></div></div> : null}
      {groups.length ? (
        <div className="note-pyramid">
          {groups.map((group) => (
            <div className="note-row" key={group.key}>
              <strong>{group.label}</strong>
              <p>{group.notes.length ? group.notes.join(" · ") : "资料未收录"}</p>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ProductDetailScreen({
  flow,
  product,
  source,
  catalog,
  openSheet,
}: {
  flow: FlowControls;
  product: Product;
  source: ProductDetailSource;
  catalog: Product[];
  openSheet: (sheet: Exclude<SheetState, null>) => void;
}) {
  const wishlist = useWishlist();
  const { detail, loading, error, reload } = useProductDetail(product);
  const [checkingSku,setCheckingSku]=useState('');
  function showSkuDestination(sku:Sku) {
    if(!detail || !skuCanOpen(sku))return false;
    const target={miniAppId:sku.miniAppId,miniProgramUrl:sku.miniProgramUrl};
    const mini=isMiniProgramChannel(window.location.search);
    if(mini && (!target.miniAppId || !target.miniProgramUrl))return false;
    if(!mini && !sku.purchaseUrl?.startsWith('https://'))return false;
    openSheet({...productDestinationSheet('查看 Beautinow 商品页',`${visibleProductTitle(detail)} · ${sku.packageType} ${sku.capacity}`,target),actionHref:!mini?sku.purchaseUrl:undefined,onAction:()=>openProductUrl(sku.purchaseUrl,target,openSheet)});
    return true;
  }
  async function openSku(sku:Sku) {
    if(!detail || checkingSku)return;
    setCheckingSku(sku.id);
    try {
      const result=await productSearchApi.skuAvailability(detail.id,sku.id);
      const latest:Sku={...sku,availability:result.sku.availability,saleStatus:result.sku.saleStatus||undefined,purchaseUrl:result.sku.purchaseUrl||undefined,miniAppId:result.sku.miniAppId||undefined,miniProgramUrl:result.sku.miniProgramUrl||undefined};
      if(!showSkuDestination(latest)) {reload();openSheet({title:'商品链接暂不可用',description:'这条规格暂时没有可用的商品链接，请稍后重试。'});}
    } catch(error){
      // A failed stock refresh does not invalidate the concrete target already loaded.
      const status=(error as {status?:number}).status;
      if(status===404 || status===403 || !showSkuDestination(sku))openSheet({title:'暂时无法打开商品',description:error instanceof Error?error.message:'请稍后重试'});
    }
    finally {setCheckingSku('');}
  }
  async function addProfile() {
    if (!detail || checkingSku) return;
    setCheckingSku('profile');
    try {
      const result = await wishlistApi.addPerfume(detail.id);
      wishlist.applyMutation(result);
      openSheet({title: result.outcome === 'already_exists' ? '已在心愿单' : '已加入心愿单', description: '可在首页心愿单查看。'});
    } catch (error) { openSheet({title:'暂时无法加入',description:error instanceof Error ? error.message : '请稍后重试。'}); }
    finally { setCheckingSku(''); }
  }
  async function retrySku(sku: Sku) {
    if (!detail || checkingSku) return;
    setCheckingSku(sku.id);
    try {
      const result = await productSearchApi.skuAvailability(detail.id, sku.id);
      const latest: Sku = {...sku,availability:result.sku.availability,saleStatus:result.sku.saleStatus || undefined,purchaseUrl:result.sku.purchaseUrl || undefined,miniAppId:result.sku.miniAppId || undefined,miniProgramUrl:result.sku.miniProgramUrl || undefined};
      reload();
      if (skuAction(latest) === 'purchase') showSkuDestination(latest);
      else if (skuAction(latest) === 'wishlist') openSheet({title:'当前暂无可售库存',description:'可以点击该规格加入心愿单。'});
      else openSheet({title:'暂时查不到库存',description:'请稍后点击“重试查询”。库存未确认不代表商品缺货。'});
    } catch (error) {openSheet({title:'暂时查不到库存',description:error instanceof Error ? error.message : '请稍后重试。'});}
    finally {setCheckingSku('');}
  }
  async function addUnavailableSku(sku: Sku) {
    if (!detail || checkingSku) return;
    setCheckingSku(sku.id);
    try {
      const result = await wishlistApi.addDetail(detail.id, sku.id);
      wishlist.applyMutation(result);
      const savedKind = result.item?.identityKind === "commerce" ? "商品" : "香水";
      openSheet({ title: result.outcome === "already_exists" ? "已在心愿单" : "已加入心愿单", description: result.outcome === "already_exists" ? `这款${savedKind}已经在你的心愿单中。` : `已记下这款${savedKind}，可在首页心愿单查看。` });
    } catch (requestError) {
      openSheet({ title: "暂时无法加入", description: requestError instanceof Error ? requestError.message : "请稍后重试。" });
    } finally { setCheckingSku(""); }
  }
  const detailMainRef = useRef<HTMLElement>(null);
  const jumpTo = (selector: string) => detailMainRef.current?.querySelector<HTMLElement>(selector)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const skuConcentrations = detail
    ? [...new Set(detail.skus.map((sku) => sku.concentration || sku.packageType).filter(Boolean))]
    : [];
  const sharedSkuConcentration = skuConcentrations.length === 1 ? skuConcentrations[0] : "";
  const sourceOnlyDetail = Boolean(detail?.sourceOnly && !detail.skus.length);
  const displayConcentration = sharedSkuConcentration || (sourceOnlyDetail ? detail?.concentration || "浓度资料未收录" : "");
  return (
    <>
    <MobileScroll className="app-screen">
      <main ref={detailMainRef} className="purchase-content product-detail-content" data-testid="product-detail" data-source={source.kind}>
        <InlineBack flow={flow} />
        {loading ? null : error || !detail ? (
          <div className="detail-state detail-error" role="alert">
            <strong>商品详情暂时无法加载</strong>
            <span>{error || "请检查网络后重试。"}</span>
            <button type="button" onClick={reload}>重新加载</button>
          </div>
        ) : (
          <>
            <header className="product-identity">
              {visibleBrand(detail) ? <span className="eyebrow">{visibleBrand(detail)}</span> : null}
              <h1 className={(detail.chineseName || detail.name).length > 28 ? "long-product-name" : ""}>{detail.chineseName || detail.name}</h1>
              {detail.chineseName ? <p className="product-english">{detail.name}</p> : null}
              <div className="product-quick-facts">{displayConcentration ? <span>{displayConcentration}</span> : null}</div>
              {detail.reviewInsights?.hasDirectReviews ? <button className="rating-jump" type="button" onClick={() => jumpTo(".product-reviews")}>{hasReviewRating(detail.reviewInsights) ? `${detail.reviewInsights.rating!.toFixed(1)} / 5 · ` : ""}查看香评 ↓</button> : null}
            </header>
            <div className="product-overview">
              <div className="product-hero"><ResilientImage className="purchase-image" sources={productImageSources(detail)} alt={visibleProductTitle(detail)} /></div>
              <ProductScentProfile product={detail} />
            </div>
            <ProductReviewSection product={detail} />
            {detail.skus.length || sourceOnlyDetail ? (
              <section className="purchase-options" aria-labelledby={`sku-title-${detail.id}`}>
                <h2 id={`sku-title-${detail.id}`}>规格</h2>
                {displayConcentration ? <p className="sku-shared-concentration">{displayConcentration}</p> : null}
                <div className="purchase-skus scrollable-skus" data-testid="sku-list" tabIndex={0} aria-label="上下滚动查看全部规格">
                  {sourceOnlyDetail ? (
                    <button type="button" disabled={Boolean(checkingSku)} onClick={() => void addProfile()}>
                      <ResilientImage className="purchase-sku-image" sources={productImageSources(detail)} alt="" />
                      <span><strong>暂无在售商品</strong><small>可先加入心愿单</small></span>
                      <span className="purchase-sku-action">{checkingSku ? "正在处理…" : "加入心愿单"} <ArrowRightIcon /></span>
                    </button>
                  ) : null}
                  {detail.skus.map((sku) => {
                    const concentration = sku.concentration || sku.packageType;
                    const primary = sharedSkuConcentration ? sku.capacity : concentration;
                    const secondary = sharedSkuConcentration
                      ? sku.packaging || ""
                      : [sku.capacity, sku.packaging].filter(Boolean).join(" · ");
                    const currentSaleCopy=saleCopy(sku);
                    const action=skuAction(sku);
                    const confirmedUnavailable=currentSaleCopy==='暂时无货'||currentSaleCopy==='已下架';
                    return (
                      <button
                        type="button"
                        key={sku.id}
                        disabled={Boolean(checkingSku) || (skuInStock(sku) && !skuCanOpen(sku))}
                        onClick={() => void (action === "purchase" ? openSku(sku) : action === "retry" ? retrySku(sku) : addUnavailableSku(sku))}
                      >
                        <ResilientImage className="purchase-sku-image" sources={skuImageSources(sku, detail)} alt="" />
                        <span><strong>{primary}</strong>{secondary ? <small>{secondary}</small> : null}<small className={confirmedUnavailable?"stock-indicator stock-empty":skuInStock(sku)?"stock-indicator":"sku-unavailable"}>{skuInStock(sku)||confirmedUnavailable?<><i/>{currentSaleCopy}</>:currentSaleCopy}</small></span>
                        <span className="purchase-sku-action">{checkingSku===sku.id?"正在处理…":action === "purchase" ? "点击查看" : action === "retry" ? "重试查询" : "加入心愿单"} <ArrowRightIcon /></span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}
            <ProductDescription product={detail} />
            <button className="similar-link" type="button" onClick={() => flow.push(makeSimilarProductsScreen(detail, catalog, openSheet))}>查看相似香水</button>
          </>
        )}
      </main>
    </MobileScroll>
    {loading ? <SearchTransitionView stage={1} /> : null}
    </>
  );
}

function makeCollectionSearchResultsScreen(
  mode: CollectionMode,
  heading: string,
  products: Product[],
  source: string,
  catalog: Product[],
  openSheet: (sheet: Exclude<SheetState, null>) => void,
  catalogPage?: CatalogCollectionPage,
  aiResult?: AIFindResponse,
): FlowScreen {
  return {
    id: `${mode}-search-${heading}`,
    render: (flow) => <CollectionSearchResultsScreen flow={flow} mode={mode} heading={heading} products={products} source={source} catalog={catalog} openSheet={openSheet} catalogPage={catalogPage} aiResult={aiResult} />,
  };
}

function PagedProductList({products,onSelect,label}:{products:Product[];onSelect:(product:Product)=>void;label:string}) {
  const [page,setPage]=useState(0);
  const [direction,setDirection]=useState('next');
  const [pageInput,setPageInput]=useState('');
  const [pageError,setPageError]=useState('');
  const top=useRef<HTMLDivElement>(null);
  const body=useRef<HTMLDivElement>(null);
  const dialog=useRef<HTMLDialogElement>(null);
  const input=useRef<HTMLInputElement>(null);
  const pageButton=useRef<HTMLButtonElement>(null);
  const retainedHeight=useRef(0);
  const pendingScroll=useRef<{node:HTMLElement;top:number}|null>(null);
  const dialogScroll=useRef<{node:HTMLElement;top:number}|null>(null);
  const current=productPage(products,page);
  function position() {
    const node=top.current?.closest<HTMLElement>('.h5-scroll');
    return node?{node,top:node.scrollTop}:null;
  }
  useLayoutEffect(()=>{
    if(body.current) {
      // Keep the pager in place on short/last pages, without clipping long names.
      retainedHeight.current=Math.max(retainedHeight.current,body.current.getBoundingClientRect().height);
      body.current.style.minHeight=`${retainedHeight.current}px`;
    }
    if(pendingScroll.current) {
      pendingScroll.current.node.scrollTop=pendingScroll.current.top;
      pendingScroll.current=null;
    }
  },[current.page,products]);
  function turn(next:number,saved=position()) {
    if(next===current.page){pendingScroll.current=null;return;}
    pendingScroll.current=saved;
    setDirection(next>current.page?'next':'previous');setPage(next);
  }
  function showJump() {
    dialogScroll.current=position();setPageInput(String(current.page+1));setPageError('');
    dialog.current?.showModal();input.current?.focus({preventScroll:true});input.current?.select();
  }
  function closeJump() {
    dialog.current?.close();pageButton.current?.focus({preventScroll:true});
    if(dialogScroll.current)dialogScroll.current.node.scrollTop=dialogScroll.current.top;
  }
  function jump(event:FormEvent) {
    event.preventDefault();
    const next=parsePageNumber(pageInput,current.pages);
    if(next===null){setPageError(`请输入 1–${current.pages} 之间的整数页码`);return;}
    const saved=dialogScroll.current;closeJump();turn(next,saved);
  }
  return <div className="paged-results" ref={top} aria-label={label}>
    <div className="paged-results-body" ref={body} style={{minHeight:retainedHeight.current||undefined}} key={current.page} data-direction={direction}>
      <div className="recommend-list unified-result-list">{current.items.map(product=><ProductResultRow key={product.id} product={product} onSelect={()=>onSelect(product)}/>)}</div>
    </div>
    {current.pages>1?<nav className="result-pagination" aria-label={`${label}翻页`}>
      <button type="button" disabled={current.page===0} onClick={()=>turn(current.page-1)} aria-label={`${label}上一页`}>← 上一页</button>
      <button ref={pageButton} type="button" className="page-number-button" onClick={showJump} aria-label={`${label}页码 ${current.page+1} / ${current.pages}，点击跳页`} aria-haspopup="dialog"><span aria-live="polite" aria-atomic="true">{current.page+1} / {current.pages}</span><small>跳页</small></button>
      <button type="button" disabled={current.page===current.pages-1} onClick={()=>turn(current.page+1)} aria-label={`${label}下一页`}>下一页 →</button>
    </nav>:null}
    <dialog className="page-jump-dialog" ref={dialog} aria-label={`${label}跳转页码`} onCancel={event=>{event.preventDefault();closeJump();}}>
      <form onSubmit={jump} noValidate>
        <h2>跳到第几页？</h2>
        <p>共 {current.pages} 页，输入你想查看的页码。</p>
        <label>页码<input ref={input} type="text" inputMode="numeric" pattern="[0-9]*" value={pageInput} onChange={event=>{setPageInput(event.target.value);setPageError('');}} aria-invalid={Boolean(pageError)} /></label>
        {pageError?<p className="page-jump-error" role="alert">{pageError}</p>:null}
        <div className="page-jump-actions"><button type="button" onClick={closeJump}>取消</button><button type="submit">跳转</button></div>
      </form>
    </dialog>
  </div>;
}

function CollectionSearchResultsScreen({flow,mode,heading,products,source,catalog,openSheet,catalogPage,aiResult}: {
  flow: FlowControls; mode: CollectionMode; heading: string; products: Product[]; source: string; catalog: Product[];
  openSheet: (sheet: Exclude<SheetState,null>)=>void; catalogPage?: CatalogCollectionPage; aiResult?: AIFindResponse;
}) {
  const [visibleProducts,setVisibleProducts]=useState(products);
  const [hasMore,setHasMore]=useState(Boolean(catalogPage?.hasMore));
  const [nextOffset,setNextOffset]=useState(catalogPage?.nextOffset || products.length);
  const [loadError,setLoadError]=useState('');
  const [loadAttempt,setLoadAttempt]=useState(0);
  const [expanded,setExpanded]=useState(false);
  const foldButton=useRef<HTMLButtonElement>(null);
  const partition=partitionSearchResults(visibleProducts,!hasMore);
  const relatedContext=useMemo<RelatedContext>(()=>mode==='brand'?{contextBrand:heading}:aiResult?(aiResult.relatedContext||{exclude:products.map(p=>p.id)}):{contextQuery:catalogPage?.query||source},[mode,heading,aiResult,products,catalogPage?.query,source]);
  const reference=visibleProducts.find(p=>p.id===(aiResult?.referenceKey || catalogPage?.referenceKey)) || visibleProducts[0];
  const select=(product: Product)=>flow.push(makeProductDetailScreen(product,{kind:mode==='image'?'recommendation':'search',label:source},catalog,openSheet));
  useEffect(()=>{
    if(!catalogPage || !hasMore)return;
    let active=true;
    setLoadError('');
    // Fetch remaining API batches independently of the five-row display pages.
    // Only one batch is in flight, and results never append to the rendered list.
    productSearchApi.search({q:catalogPage.query,limit:200,offset:nextOffset,confirmBrand:catalogPage.confirmBrand,literal:catalogPage.literal})
      .then(payload=>{
        if(!active)return;
        if(payload.hasMore && (!payload.nextOffset || payload.nextOffset<=nextOffset))throw new Error('更多结果暂未加载完成，请重试。');
        setVisibleProducts(current=>uniqueProducts([...current,...toProducts(payload.products)],Number.MAX_SAFE_INTEGER));
        setNextOffset(payload.nextOffset || nextOffset);setHasMore(Boolean(payload.hasMore));
      }).catch(error=>{if(active)setLoadError(error instanceof Error?error.message:'更多结果暂未加载完成');});
    return()=>{active=false;};
  },[catalogPage,hasMore,nextOffset,loadAttempt]);
  function collapse() {
    setExpanded(false);
    foldButton.current?.focus({preventScroll:true});
    foldButton.current?.scrollIntoView({behavior:'smooth',block:'nearest'});
  }
  return <MobileScroll className="app-screen search-paged-screen"><main className="result-content brand-search-results" data-testid="brand-search-results">
    <InlineBack flow={flow}/><ResultSearchBar flow={flow} initialValue={source} catalog={catalog} openSheet={openSheet}/>
    <h1>{mode==='keyword'?`包含“${heading}”的商品`:heading}</h1>
    {aiResult?.reason ? <p className="ai-recommendation-reason">{aiResult.reason}</p>:null}
    <section className="standard-product-results" aria-label="搜索结果">
      {(expanded?[...partition.visible,...partition.folded]:partition.visible).length?<PagedProductList key={expanded?'expanded':'collapsed'} products={expanded?[...partition.visible,...partition.folded]:partition.visible} onSelect={select} label="商品"/>:null}
      {!visibleProducts.length&&!hasMore?<p className="section-intro">{aiResult?.emptyMessage || '暂未找到匹配商品，可以换一张照片或改用品牌、香水名搜索。'}</p>:null}
      {partition.folded.length ? <>
        <button ref={foldButton} className="fold-products" type="button" aria-expanded={expanded} onClick={()=>expanded?collapse():setExpanded(true)}>{expanded?'收起其他无货商品':'展开其他无货商品'}</button>
      </>:null}
      {hasMore?<div className="search-page-loading" role="status">{loadError?<>{loadError} <button type="button" onClick={()=>setLoadAttempt(value=>value+1)}>重新加载</button></>:'正在加载更多结果…'}</div>:null}
    </section>
    {reference?<RelatedProducts reference={reference} initial={aiResult?toProducts(aiResult.relatedProducts):undefined} initialStatus={aiResult?.relatedStatus} initialPoolToken={aiResult?.relatedPoolToken} context={relatedContext} onSelect={select} onAll={(token)=>flow.push(makeSimilarProductsScreen(reference,catalog,openSheet,relatedContext,token))}/>:null}
  </main></MobileScroll>;
}

const scentFamilies = [
  "不限香调", "柑橘调", "芳香调", "花香调", "果香调", "绿叶调", "水生调", "木质调",
  "馥奇调", "西普调", "东方调", "琥珀调", "皮革调", "美食调", "麝香调", "醛香调", "烟草调",
];
const budgetOptions = ["不限预算", "¥200以内", "¥200-400", "¥400-800", "¥800-1,500", "¥1,500以上"];

type DetailField = { key: string; label: string; options?: string[]; placeholder?: string };

const purposeFields: Record<RecommendationPurpose, DetailField[]> = {
  "自己使用": [
    { key: "gender", label: "性别", options: ["女生", "男生", "不限定"] },
    { key: "age", label: "年龄", options: ["18-24", "25-34", "35-44", "45岁以上"] },
    { key: "job", label: "职业", placeholder: "例如：设计师、金融、学生" },
    { key: "style", label: "日常风格", options: ["简约", "知性", "松弛", "个性", "成熟"] },
  ],
  "送礼": [
    { key: "recipient", label: "送给谁", options: ["女生", "男生", "不限定"] },
    { key: "age", label: "对方年龄", options: ["18-24", "25-34", "35-44", "45岁以上"] },
    { key: "relation", label: "你们的关系", options: ["伴侣", "朋友", "家人", "同事／客户"] },
    { key: "occasion", label: "赠礼场合", options: ["生日", "纪念日", "节日", "商务", "日常心意"] },
  ],
  "约会": [
    { key: "dateTarget", label: "约会对象", options: ["女生", "男生", "不限定"] },
    { key: "mood", label: "想要的氛围", options: ["亲近温柔", "轻松自然", "成熟迷人", "清爽有距离"] },
    { key: "time", label: "约会时间", options: ["白天", "夜晚", "全天"] },
    { key: "place", label: "约会场景", placeholder: "例如：晚餐、看展、户外散步" },
  ],
  "通勤": [
    { key: "industry", label: "职业／行业", placeholder: "例如：互联网、金融、教育" },
    { key: "space", label: "办公环境", options: ["开放办公室", "独立办公室", "经常见客户", "户外／差旅"] },
    { key: "dress", label: "着装风格", options: ["休闲", "商务", "创意自由", "制服"] },
    { key: "presence", label: "希望呈现", options: ["干净可靠", "低调克制", "有亲和力", "有辨识度"] },
  ],
  "其他场景": [
    { key: "scene", label: "使用场景", placeholder: "请描述旅行、运动、聚会或其他用途" },
    { key: "forWhom", label: "使用者", options: ["自己", "女生", "男生", "不限定"] },
    { key: "season", label: "季节", options: ["春夏", "秋冬", "四季"] },
    { key: "wish", label: "希望给人的印象", placeholder: "例如：温柔、专业、神秘、松弛" },
  ],
};

function makeRecommendationScreen(catalog: Product[], openSheet: (sheet: Exclude<SheetState, null>) => void): FlowScreen {
  return {
    id: "recommendation-brief",
    render: (flow) => <RecommendationScreen flow={flow} catalog={catalog} openSheet={openSheet} />,
  };
}

function RecommendationScreen({ flow, catalog, openSheet }: { flow: FlowControls; catalog: Product[]; openSheet: (sheet: Exclude<SheetState, null>) => void }) {
  const [draftRevision, setDraftRevision] = useState(0);
  const savedDraft = useMemo(() => loadRecommendationDraft(), [draftRevision]);
  const hasSavedChoices = Boolean(
    savedDraft.freeText.trim()
    || savedDraft.activePurpose
    || Object.values(savedDraft.scenarioAnswers).some((answers) => Object.keys(answers).length)
    || savedDraft.preferences.scents.some((scent) => scent !== "不限香调")
    || savedDraft.preferences.budget !== "不限预算"
    || savedDraft.preferences.brand !== "不限品牌",
  );
  const resetSavedChoices = () => openSheet({
    title: "重新开始选香",
    description: "将清除这台设备上已保存的描述、场景、香调、预算和品牌选择。",
    actionLabel: "清除选择",
    onAction: () => {
      clearRecommendationDraft();
      setDraftRevision((value) => value + 1);
    },
  });
  return (
    <MobileScroll className="app-screen">
      <main className="recommend-content" data-testid="recommendation-form">
        <InlineBack flow={flow} />
        <h1>帮你找到适合的香水</h1>
        <p className="section-intro">选择一种更适合你的表达方式。</p>

        <div className="advisor-options">
          <button className="advisor-option" type="button" onClick={() => flow.push(makeSmartDescriptionScreen(catalog, openSheet))}>
            <span><strong>直接描述需求</strong><small>适合已经能说出场景、气味或预算的你。</small></span><ArrowRightIcon />
          </button>
          <button className="advisor-option" type="button" onClick={() => flow.push(makePurposeSelectionScreen(catalog, openSheet))}>
            <span><strong>一步步选择</strong><small>适合还不知道怎样描述、希望获得引导的你。</small></span><ArrowRightIcon />
          </button>
        </div>
        {hasSavedChoices ? <button className="clear-saved-recommendation" type="button" onClick={resetSavedChoices}>清除已保存选择</button> : null}
      </main>
    </MobileScroll>
  );
}

function makePurposeSelectionScreen(catalog: Product[], openSheet: (sheet: Exclude<SheetState, null>) => void): FlowScreen {
  return {
    id: "purpose-selection",
    render: (flow) => <PurposeSelectionScreen flow={flow} catalog={catalog} openSheet={openSheet} />,
  };
}

function PurposeSelectionScreen({ flow, catalog, openSheet }: { flow: FlowControls; catalog: Product[]; openSheet: (sheet: Exclude<SheetState, null>) => void }) {
  return (
    <MobileScroll className="app-screen">
      <main className="recommend-content purpose-selection-screen" data-testid="purpose-selection">
        <InlineBack flow={flow} />
        <h1>这支香准备用在哪里？</h1>
        <div className="purpose-list">
          {recommendationPurposes.map((purpose) => (
            <button type="button" key={purpose} onClick={() => {
              updateRecommendationDraft((draft) => ({ ...draft, mode: "guided", activePurpose: purpose }));
              flow.push(makePurposeDetailScreen(purpose, catalog, openSheet));
            }}>
              <span><strong>{purpose}</strong><small>{purposeHint(purpose)}</small></span><ArrowRightIcon />
            </button>
          ))}
        </div>
      </main>
    </MobileScroll>
  );
}

function makeSmartDescriptionScreen(catalog: Product[], openSheet: (sheet: Exclude<SheetState, null>) => void, initialImage = ""): FlowScreen {
  return {
    id: "smart-description",
    render: (flow) => <SmartDescriptionScreen flow={flow} catalog={catalog} openSheet={openSheet} initialImage={initialImage} />,
  };
}

function SmartDescriptionScreen({flow,catalog,openSheet,initialImage=""}:{flow:FlowControls;catalog:Product[];openSheet:(sheet:Exclude<SheetState,null>)=>void;initialImage?:string}) {
  const [description,setDescription]=useState(()=>loadRecommendationDraft().freeText);
  const [image,setImage]=useState(initialImage);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const input=useRef<HTMLInputElement>(null);
  const pending=useRef(false);
  const active=useRef(true);
  useEffect(()=>{active.current=true;return()=>{active.current=false;};},[]);
  const updateDescription = (value:string) => {setDescription(value);updateRecommendationDraft(draft=>({...draft,mode:'smart',freeText:value}));};
  async function runRecommendation() {
    if(pending.current || (!description.trim()&&!image))return;
    pending.current=true;setBusy(true);setError('');
    updateRecommendationDraft(draft=>({...draft,mode:'smart',freeText:description}));
    try {
      const result=await productSearchApi.aiFind({text:description.trim(),imageDataUrl:image});
      if(!active.current)return;
      const rows=flattenAIFindProducts(result);const products=toProducts(rows);const source=result.searchPage?.query || cleanAIFindQuery(result.displayQuery || result.query);
      flow.push(makeCollectionSearchResultsScreen(result.searchPage?(result.searchPage.confirmBrand?'brand':'keyword'):'image',result.searchPage?source:imageResultTitle(result,products.length),products,source,catalog,openSheet,result.searchPage||undefined,{...result,products:rows}));
    } catch(err){setError(aiErrorCopy(err));}
    finally {pending.current=false;setBusy(false);}
  }
  return <><MobileScroll className="app-screen"><main className="recommend-content smart-description-screen" data-testid="smart-description-screen">
    <InlineBack flow={flow}/><h1>像和香水顾问聊天一样描述</h1>
    <p className="section-intro">说说你想找什么样的香水。也可以发张香水、穿搭或场景照片，让我们帮你选。</p>
    <KeyboardTextarea value={description} disabled={busy} onChange={event=>updateDescription(event.target.value)} placeholder="比如：上班用，闻起来干净清爽，不要太甜。" aria-label="描述你的香水需求"/>
    <input hidden ref={input} type="file" accept="image/jpeg,image/png,image/webp" onChange={async event=>{const file=event.target.files?.[0];event.target.value='';if(!file)return;try {setImage(await readImageFile(file));setError('');} catch(err){setError(err instanceof Error?err.message:'图片无法读取');}}}/>
    <div className="photo-upload-zone">
      <button className="photo-upload" type="button" disabled={busy} onClick={()=>input.current?.click()}>
        {image?<img className="photo-thumbnail" src={image} alt="本次上传的照片"/>:<CameraIcon/>}
        <span><strong>{image?'已添加一张照片':'添加一张照片'}</strong><small>{image?'点击更换照片':'拍照或从相册选择'}</small></span>
      </button>
      {image?<button className="remove-photo" type="button" disabled={busy} aria-label="删除图片" onClick={()=>setImage('')}><Cross2Icon/></button>:null}
    </div>
    {error?<p className="clarification-error" role="alert">{error}</p>:null}
    <button className="primary-action" type="button" disabled={busy||(!description.trim()&&!image)} onClick={runRecommendation}>{busy?'正在分析…':'开始识别推荐'} <ArrowRightIcon/></button>
  </main></MobileScroll>{busy?<SearchTransitionView stage={0}/>:null}</>;
}

function purposeHint(purpose: RecommendationPurpose) {
  return ({
    "自己使用": "年龄、职业、性别与个人风格",
    "送礼": "收礼对象、年龄、关系与场合",
    "约会": "对象、时间、地点与氛围",
    "通勤": "行业、环境、着装与气质",
    "其他场景": "旅行、聚会、运动或自定义用途",
  } as Record<string, string>)[purpose];
}

function makePurposeDetailScreen(purpose: RecommendationPurpose, catalog: Product[], openSheet: (sheet: Exclude<SheetState, null>) => void): FlowScreen {
  return {
    id: `purpose-${purpose}`,
    render: (flow) => <PurposeDetailScreen flow={flow} purpose={purpose} catalog={catalog} openSheet={openSheet} />,
  };
}

function PurposeDetailScreen({ flow, purpose, catalog, openSheet }: { flow: FlowControls; purpose: RecommendationPurpose; catalog: Product[]; openSheet: (sheet: Exclude<SheetState, null>) => void }) {
  const [answers, setAnswers] = useState<Record<string, string>>(() => loadRecommendationDraft().scenarioAnswers[purpose]);
  const fields = purposeFields[purpose];
  const updateAnswer = (key: string, value: string) => setAnswers((current) => {
    const next = { ...current, [key]: value };
    updateRecommendationDraft((draft) => ({
      ...draft,
      mode: "guided",
      activePurpose: purpose,
      scenarioAnswers: { ...draft.scenarioAnswers, [purpose]: next },
    }));
    return next;
  });
  return (
    <MobileScroll className="app-screen">
      <main className="recommend-content scenario-content" data-testid="scenario-details">
        <InlineBack flow={flow} />
        <h1>{purpose}</h1>
        {fields.map((field) => (
          <fieldset className="choice-group detail-group" key={field.key}>
            <legend>{field.label}</legend>
            {field.options ? (
              <div>
                {field.options.map((option) => (
                  <button type="button" key={option} data-selected={answers[field.key] === option ? "true" : "false"} onClick={() => updateAnswer(field.key, option)}>{option}</button>
                ))}
              </div>
            ) : (
              <KeyboardInput
                className="detail-input"
                value={answers[field.key] || ""}
                onChange={(event) => updateAnswer(field.key, event.target.value)}
                placeholder={field.placeholder}
                aria-label={field.label}
              />
            )}
          </fieldset>
        ))}
        <button className="primary-action" type="button" onClick={() => flow.push(makePreferenceScreen(catalog, {
          purpose,
          details: Object.values(answers).filter(Boolean),
          answers,
          scent: "不限香调",
          budget: "不限预算",
          brand: "不限品牌",
        }, openSheet))}>
          继续选择香调与预算 <ArrowRightIcon />
        </button>
      </main>
    </MobileScroll>
  );
}

function makePreferenceScreen(catalog: Product[], base: RecommendationContext, openSheet: (sheet: Exclude<SheetState, null>) => void): FlowScreen {
  return {
    id: `preferences-${base.purpose}`,
    render: (flow) => <PreferenceScreen flow={flow} catalog={catalog} base={base} openSheet={openSheet} />,
  };
}

function PreferenceScreen({ flow, catalog, base, openSheet }: { flow: FlowControls; catalog: Product[]; base: RecommendationContext; openSheet: (sheet: Exclude<SheetState, null>) => void }) {
  const [savedDraft] = useState(() => loadRecommendationDraft());
  const [scents, setScents] = useState<string[]>(savedDraft.preferences.scents);
  const [budget, setBudget] = useState(savedDraft.preferences.budget);
  const [brand, setBrand] = useState(savedDraft.preferences.brand);
  const brandDirectory = useBrandDirectory();
  const brands = useMemo(() => recommendableBrands(brandDirectory.brands).slice(0, 5), [brandDirectory.brands]);
  const selectedBrand = useMemo(
    () => brandDirectory.brands.find((option) => normalizeBrandText(option.name) === normalizeBrandText(brand)),
    [brand, brandDirectory.brands],
  );
  const toggleScent = (value: string) => setScents((current) => {
    const next = value === "不限香调"
      ? [value]
      : (() => {
        const withoutAny = current.filter((item) => item !== "不限香调");
        return withoutAny.includes(value)
          ? (withoutAny.filter((item) => item !== value).length ? withoutAny.filter((item) => item !== value) : ["不限香调"])
          : [...withoutAny, value];
      })();
    updateRecommendationDraft((draft) => ({ ...draft, preferences: { ...draft.preferences, scents: next } }));
    return next;
  });
  const selectBudget = (value: string) => {
    setBudget(value);
    updateRecommendationDraft((draft) => ({ ...draft, preferences: { ...draft.preferences, budget: value } }));
  };
  const selectBrand = (value: string) => {
    setBrand(value);
    updateRecommendationDraft((draft) => ({ ...draft, preferences: { ...draft.preferences, brand: value } }));
  };
  return (
    <MobileScroll className="app-screen">
      <main className="recommend-content" data-testid="preference-form">
        <InlineBack flow={flow} />
        <h1>把范围收窄到刚刚好</h1>
        <p className="section-intro">香调可以多选，其他选项可以不限。</p>

        <fieldset className="choice-group">
          <legend>偏好香调</legend>
          <div>{scentFamilies.map((value) => <button type="button" key={value} data-selected={scents.includes(value) ? "true" : "false"} onClick={() => toggleScent(value)}>{value}</button>)}</div>
        </fieldset>
        <fieldset className="choice-group">
          <legend>预算</legend>
          <div>{budgetOptions.map((value) => <button type="button" key={value} data-selected={budget === value ? "true" : "false"} onClick={() => selectBudget(value)}>{value}</button>)}</div>
        </fieldset>
        <fieldset className="choice-group">
          <legend>品牌</legend>
          {brand !== "不限品牌" ? (
            <p className="current-brand-selection">
              当前选择 <strong>{[selectedBrand?.nameEn || selectedBrand?.name || brand, selectedBrand?.nameCn].filter(Boolean).join(" · ")}</strong>
              {selectedBrand && !selectedBrand.recommendableCount ? <small>暂未匹配到可推荐香水，仍可继续查看其他品牌建议</small> : null}
            </p>
          ) : null}
          <div>
            <button type="button" data-selected={brand === "不限品牌" ? "true" : "false"} onClick={() => selectBrand("不限品牌")}>不限品牌</button>
            {brands.map((option) => <button type="button" key={option.name} data-selected={normalizeQueryText(brand) === normalizeQueryText(option.name) ? "true" : "false"} onClick={() => selectBrand(option.name)}>{[option.nameEn || option.name, option.nameCn].filter(Boolean).join(" · ")}</button>)}
            <button className="more-brand-button" type="button" onClick={() => flow.push(makeBrandCatalogScreen(selectBrand))}>查看更多品牌 <ArrowRightIcon /></button>
          </div>
          {brandDirectory.loading && !brands.length ? <p className="brand-directory-state">正在加载品牌…</p> : null}
        </fieldset>
        <button className="primary-action" type="button" onClick={() => flow.push(makeRecommendationsResultScreen(catalog, { ...base, scent: scents.join("、"), budget, brand }, openSheet))}>
          查看 3-5 款推荐 <ArrowRightIcon />
        </button>
      </main>
    </MobileScroll>
  );
}

function availableBrands(catalog: Product[], recommendableOnly = false) {
  const values = [...new Set(catalog
    .filter((product) => product.isListed && product.brandVerified !== false && (!recommendableOnly || product.recommendable !== false))
    .map((product) => product.brand)
    .filter(Boolean))].sort();
  return values;
}

function makeBrandCatalogScreen(onSelect: (brand: string) => void): FlowScreen {
  return {
    id: "all-brands",
    render: (flow) => <BrandCatalogScreen flow={flow} onSelect={onSelect} />,
  };
}

function BrandCatalogScreen({ flow, onSelect }: { flow: FlowControls; onSelect: (brand: string) => void }) {
  const directory = useBrandDirectory();
  const [query, setQuery] = useState("");
  const popular = useMemo(() => recommendableBrands(directory.brands).slice(0, 8), [directory.brands]);
  const filtered = useMemo(() => filterBrandOptions(directory.brands, query), [directory.brands, query]);
  const groups = useMemo(() => groupBrandOptions(filtered), [filtered]);

  const select = (brand: BrandOption) => {
    onSelect(brand.name);
    flow.pop();
  };
  const renderBrand = (brand: BrandOption) => (
    <button type="button" key={brand.name} onClick={() => select(brand)}>
      <span className="brand-name-pair"><strong>{brand.nameEn || brand.name}</strong>{brand.nameCn ? <small>{brand.nameCn}</small> : null}</span>
      <span>{brand.recommendableCount ? `${brand.recommendableCount} 款香水` : "暂未匹配"} <ArrowRightIcon /></span>
    </button>
  );

  return (
    <MobileScroll className="app-screen">
      <main className="recommend-content brand-catalog" data-testid="brand-catalog">
        <InlineBack flow={flow} />
        <h1>选择品牌</h1>
        <label className="brand-search">
          <MagnifyingGlassIcon />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索中文或英文品牌名" aria-label="搜索品牌" />
          {query ? <button type="button" onClick={() => setQuery("")} aria-label="清空品牌搜索"><Cross2Icon /></button> : null}
        </label>

        {!query && popular.length ? (
          <section className="popular-brands" aria-labelledby="popular-brand-title">
            <h2 id="popular-brand-title">热门品牌</h2>
            <div>{popular.map((brand) => <button type="button" key={brand.name} onClick={() => select(brand)}>{brand.nameEn || brand.name}{brand.nameCn ? <small>{brand.nameCn}</small> : null}</button>)}</div>
          </section>
        ) : null}

        {directory.loading && !directory.brands.length ? <SearchTransitionView stage={1} /> : null}
        {directory.error && !directory.brands.length ? (
          <div className="catalog-error" role="status"><strong>品牌加载失败</strong><span>{directory.error}</span><button type="button" onClick={directory.reload}>重新加载</button></div>
        ) : null}
        {!directory.loading && !directory.error && !groups.length ? <div className="brand-directory-state">没有找到相关品牌</div> : null}

        {groups.length ? (
          <section className="all-brand-directory" aria-labelledby="all-brand-title">
            <h2 id="all-brand-title">{query ? "搜索结果" : "全部品牌"}</h2>
            <div className="brand-directory-layout">
              <div className="brand-groups">
                {groups.map((group) => (
                  <section key={group.initial} id={`brand-group-${group.initial === "#" ? "other" : group.initial}`} className="brand-group">
                    <h3>{group.initial}</h3>
                    <div className="brand-list">{group.brands.map(renderBrand)}</div>
                  </section>
                ))}
              </div>
              {!query ? <nav className="brand-index" aria-label="品牌首字母索引">{groups.map((group) => <a key={group.initial} href={`#brand-group-${group.initial === "#" ? "other" : group.initial}`}>{group.initial}</a>)}</nav> : null}
            </div>
          </section>
        ) : null}
      </main>
    </MobileScroll>
  );
}

function makeRecommendationsResultScreen(catalog: Product[], context: RecommendationContext, openSheet: (sheet: Exclude<SheetState, null>) => void): FlowScreen {
  return {
    id: `recommendations-${context.purpose}`,
    render: (flow) => <RecommendationsResultScreen flow={flow} catalog={catalog} context={context} openSheet={openSheet} />,
  };
}

function RecommendationsResultScreen({ flow, catalog, context, openSheet }: { flow: FlowControls; catalog: Product[]; context: RecommendationContext; openSheet: (sheet: Exclude<SheetState, null>) => void }) {
  const [batch, setBatch] = useState(0);
  const [remoteRecommendations, setRemoteRecommendations] = useState<Product[]>([]);
  const [remoteHasMore, setRemoteHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError("");
    productSearchApi.recommendations({ context, batch, count: 5 })
      .then((payload) => {
        if (disposed) return;
        setRemoteRecommendations(toProducts(payload.products).slice(0, 5));
        setRemoteHasMore(Boolean(payload.hasMore));
      })
      .catch((requestError) => {
        if (disposed) return;
        setRemoteRecommendations(fixturesEnabled ? fallbackProducts.slice(0, 4) : []);
        setRemoteHasMore(false);
        setError(requestError instanceof Error ? requestError.message : "推荐服务暂时不可用");
      })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [batch, context, revision]);
  const brandWasSelected = hasSelectedBrand(context.brand);
  const sections = useMemo(() => partitionRecommendations(remoteRecommendations, context.brand), [remoteRecommendations, context.brand]);
  const contextSummary = recommendationContextSummary(context);
  const renderRecommendations = (products: Product[]) => (
    <div className="recommend-list unified-result-list">
      {products.map((product) => (
        <ProductResultRow
          key={`${batch}-${product.id}`}
          product={product}
          onSelect={() => flow.push(makeProductDetailScreen(
            product,
            { kind: "recommendation", label: contextSummary ? `选香条件：${contextSummary}` : "来自选香推荐" },
            catalog,
            openSheet,
          ))}
        />
      ))}
    </div>
  );
  return (
    <MobileScroll className="app-screen">
      <main className="recommend-results" data-testid="recommendation-results">
        <InlineBack flow={flow} />
        <h1>这几支香，更接近你的描述</h1>
        {contextSummary ? <p className="result-context">{contextSummary}</p> : null}
        {error ? <div className="catalog-error" role="status"><strong>推荐暂时无法完成</strong><span>{error}</span><button type="button" onClick={() => setRevision((value) => value + 1)}>重新推荐</button></div> : null}
        {!loading && !error && brandWasSelected && (!sections.selectedBrand.length || sections.alternatives.length) ? (
          <section className="brand-match-summary" role="status">
            <span>{sections.selectedBrand.length
              ? `${context.brand} 的合适选择较少，以下同时展示其他品牌。`
              : `没有找到符合条件的 ${context.brand}，以下展示其他品牌。`}</span>
          </section>
        ) : null}

        {brandWasSelected && sections.selectedBrand.length ? (
          <section className="recommendation-section" aria-labelledby="selected-brand-results">
            <h2 id="selected-brand-results">{context.brand}</h2>
            {renderRecommendations(sections.selectedBrand)}
          </section>
        ) : null}

        {brandWasSelected && sections.alternatives.length ? (
          <section className="recommendation-section alternative-recommendations" aria-labelledby="alternative-brand-results">
            <div className="recommend-section-divider"><span id="alternative-brand-results">其他品牌</span></div>
            {renderRecommendations(sections.alternatives)}
          </section>
        ) : null}

        {!brandWasSelected ? renderRecommendations(sections.selectedBrand) : null}
        {remoteHasMore ? <button className="refresh-batch" type="button" disabled={loading} onClick={() => setBatch((value) => value + 1)}>换一批 <ArrowRightIcon /></button> : null}
        {loading ? <SearchTransitionView stage={1} /> : null}
      </main>
    </MobileScroll>
  );
}

function developmentFixtureRankCatalog(catalog: Product[], context: RecommendationContext) {
  const scentTokens = context.scent.split("、").filter((value) => !value.startsWith("不限"));
  const detailText = `${context.details.join(" ")} ${context.description || ""} ${context.purpose}`.toLowerCase();
  const [budgetMin, budgetMax] = budgetRange(context.budget);
  const directGender = context.answers?.gender || context.answers?.recipient || context.answers?.forWhom || "";
  const targetGender = /男生|男士|男性|男香/.test(`${directGender} ${context.description || ""}`)
    ? "男香"
    : /女生|女士|女性|女香/.test(`${directGender} ${context.description || ""}`)
      ? "女香"
      : "";
  const budgetSelected = context.budget !== "不限预算";
  return catalog
    .filter((product) => product.isListed
      && product.recommendable !== false
      && (product.productType || "perfume") === "perfume"
      && !(targetGender === "男香" && product.gender === "女香")
      && !(targetGender === "女香" && product.gender === "男香"))
    .map((product) => {
      const searchable = `${product.brand} ${product.name} ${product.chineseName} ${product.tags.join(" ")} ${product.reason}`.toLowerCase();
      const price = 0;
      const genderPriority = !targetGender ? 1 : product.gender === targetGender ? 2 : 1;
      const brandPriority = context.brand === "不限品牌" || normalizeQueryText(product.brand) === normalizeQueryText(context.brand) ? 1 : 0;
      const budgetPriority = 1;
      const scentPriority = scentTokens.filter((token) => searchable.includes(token.replace("调", ""))).length;
      let otherPriority = product.tags.filter((tag) => detailText.includes(tag.toLowerCase()) || searchable.includes(context.purpose.replace("自己使用", "日常"))).length * 7;
      if (context.description) otherPriority += context.description.split(/[，。,.\s]+/).filter((token) => token.length >= 2 && searchable.includes(token.toLowerCase())).length * 8;
      return { product, price, genderPriority, brandPriority, budgetPriority, scentPriority, otherPriority };
    })
    .sort((a, b) => b.genderPriority - a.genderPriority
      || b.brandPriority - a.brandPriority
      || b.budgetPriority - a.budgetPriority
      || (budgetSelected && !a.budgetPriority && !b.budgetPriority ? a.price - b.price : 0)
      || b.scentPriority - a.scentPriority
      || b.otherPriority - a.otherPriority
      || Number(b.product.hasStock) - Number(a.product.hasStock)
      || a.price - b.price)
    .map((item) => item.product);
}

function budgetRange(value: string): [number, number] {
  if (value === "¥200以内") return [0, 200];
  if (value === "¥200-400") return [200, 400];
  if (value === "¥400-800") return [400, 800];
  if (value === "¥800-1,500") return [800, 1500];
  if (value === "¥1,500以上") return [1500, Number.POSITIVE_INFINITY];
  return [0, Number.POSITIVE_INFINITY];
}

function inferScent(text: string) {
  return scentFamilies.find((scent) => scent !== "不限香调" && text.includes(scent.replace("调", ""))) || "不限香调";
}

function inferBudget(text: string) {
  const amount = Number(text.match(/(\d{3,5})\s*元?/)?.[1] || 0);
  if (!amount) return "不限预算";
  return budgetOptions.find((option) => { const [min, max] = budgetRange(option); return amount >= min && amount <= max; }) || "不限预算";
}

function inferBrand(text: string, catalog: Product[]) {
  return availableBrands(catalog).find((brand) => text.toLowerCase().includes(brand.toLowerCase())) || "不限品牌";
}

function inferPurpose(text: string) {
  if (/送礼|礼物|送给/.test(text)) return "送礼";
  if (/约会|晚餐|看展/.test(text)) return "约会";
  if (/通勤|上班|办公室|客户/.test(text)) return "通勤";
  if (/旅行|运动|聚会|派对/.test(text)) return "其他场景";
  return "自己使用";
}

function inferSmartAnswers(text: string): Record<string, string> {
  const answers: Record<string, string> = {};
  if (/男生|男士|男性|男香/.test(text)) answers.gender = "男生";
  if (/女生|女士|女性|女香/.test(text)) answers.gender = "女生";
  const age = Number(text.match(/(\d{2})\s*岁/)?.[1] || 0);
  if (age) answers.age = age < 25 ? "18-24" : age < 35 ? "25-34" : age < 45 ? "35-44" : "45岁以上";
  const job = text.match(/(设计师|金融|学生|教师|老师|医生|律师|程序员|工程师|销售)/)?.[1];
  if (job) answers.job = job;
  const style = ["简约", "知性", "松弛", "个性", "成熟"].find((value) => text.includes(value));
  if (style) answers.style = style;
  return answers;
}

async function findProductFromCatalog(query: string, options?: { confirmBrand?: boolean; literal?: boolean }): Promise<SearchResolution> {
  const payload: SearchResponse = await productSearchApi.search({
    q: query,
    limit: 200,
    confirmBrand: options?.confirmBrand,
    literal: options?.literal,
  });
  const matches = toProducts(payload.products);
  const page: CatalogCollectionPage = {
    query,
    total: Number(payload?.total || matches.length),
    hasMore: Boolean(payload?.hasMore),
    nextOffset: Number(payload?.nextOffset || matches.length),
    referenceKey: payload.primaryProductKey,
    confirmBrand: options?.confirmBrand,
    literal: options?.literal,
    matchType: payload.matchType,
    originalQuery: payload.originalQuery || query,
    resolvedQuery: payload.resolvedQuery || query,
  };
  if (payload?.searchMode === "brand_confirm") {
    const brands = Array.isArray(payload?.brands) ? payload.brands : [];
    return {
      mode: "clarify",
      suggestions: brands.slice(0, 5).map((brand) => ({
        query: brand.name,
        label: [brand.nameEn, brand.nameCn].filter(Boolean).join(" ") || brand.name,
        hint: `确认后查看全部 ${brand.count || 0} 款收录香水`,
        kind: "品牌" as const,
        matchType: brand.matchType || payload.matchType,
      })),
    };
  }
  if (payload?.searchMode === "brand" && matches.length) {
    return { mode: "brand", brand: String(payload.brand || query), products: matches, page };
  }
  if (payload?.searchMode === "category") {
    return { mode: "category", category: String(payload.category || query), products: matches, page };
  }
  if (payload?.searchMode === "promotion") {
    return {
      mode: "promotion",
      keyword: String(payload.keyword || query),
      collection: String(payload.collection || "近期活动"),
      products: matches,
      page,
    };
  }
  if (payload?.searchMode === "keyword") {
    return { mode: "keyword", keyword: String(payload.keyword || query), products: matches, page };
  }
  if ("primaryProductKey" in payload) return {mode:"keyword",keyword:query,products:matches,page};
  if (matches[0]) {
    const needle = normalizeQueryText(query);
    const first = matches[0];
    const brand = normalizeQueryText(first.brand);
    const name = normalizeQueryText(first.name);
    const chineseName = normalizeQueryText(first.chineseName);
    const combinations = [brand, name, chineseName, `${brand}${name}`, `${brand}${chineseName}`, `${name}${brand}`, `${chineseName}${brand}`].filter(Boolean);
    const containsDistinctiveName = [name, chineseName].some((value) => value.length >= 3 && needle.includes(value));
    const highConfidence = combinations.includes(needle) || combinations.some((value) => value.includes(needle)) || containsDistinctiveName;
    if (!highConfidence) {
      const seenSuggestions = new Set<string>();
      const suggestions = matches.map<SearchSuggestion>((product) => ({
        query: [visibleBrand(product), product.name].filter(Boolean).join(" "),
        label: [visibleBrand(product), product.chineseName || product.name].filter(Boolean).join(" "),
        hint: product.chineseName ? product.name : "可能的商品名称",
        kind: "商品",
      })).filter((suggestion) => {
        const key = normalizeQueryText(suggestion.query);
        if (seenSuggestions.has(key)) return false;
        seenSuggestions.add(key);
        return true;
      }).slice(0, 5);
      return {
        mode: "clarify",
        suggestions,
      };
    }
    return { mode: "product", product: first };
  }
  throw new Error("no matching product");
}

function productTarget(product: Product, sku?: Sku) {
  return {
    miniAppId: sku?.miniAppId || product.miniAppId,
    miniProgramUrl: sku?.miniProgramUrl || product.miniProgramUrl,
  };
}

function openProductUrl(
  url: string | undefined,
  target: ReturnType<typeof productTarget>,
  openSheet: (sheet: Exclude<SheetState, null>) => void,
) {
  const result = openProductDestination(url, target);
  if (result !== "unavailable") return;
  const miniProgram = isMiniProgramChannel(window.location.search);
  openSheet({
    title: miniProgram ? "Beautinow 小程序暂时无法打开" : "商品页暂时无法打开",
    description: miniProgram
      ? "请返回当前搜索结果稍后重试；Search 小程序不会降级打开商品 H5 页面。"
      : "请检查网络后重试。",
  });
}

function productDestinationSheet(title: string, description: string, target: ReturnType<typeof productTarget>) {
  if (isMiniProgramChannel(window.location.search)) {
    const hasProductTarget = Boolean(target.miniAppId && target.miniProgramUrl);
    return {
      title: "进入 Beautinow 小程序",
      description: hasProductTarget
        ? "将进入 Beautinow 小程序查看这款香水。当前搜索结果会保留。"
        : "这款香水暂未配置小程序商品页，将进入 Beautinow 小程序首页。当前搜索结果会保留。",
      actionLabel: "继续进入",
    };
  }
  return { title, description, actionLabel: "前往 Beautinow 商品页" };
}
